import { type FormEvent, type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "bootstrap";
import {
  addUser,
  deleteUser,
  generateReport,
  getAllUsers,
  getAllUsersReport,
  reportAI,
  reportGithubChangesByProject,
  tasksByUser,
  getAllTasks,
  getAllClientGroups,
  generateReportByTask,
  generateReportByClientGroup,
  getTracking,
  uploadSheets,
  getDashboard,
  getAvailableDates,
  getAllUsersWeeklyReport,
  generateReportByTeamMemberSummary,
  generateReportByOpenJobsByClientGroup,
  generateReportByOpenJobsByJobName,
  generateClientGroupUserHoursReport,
  generateReportByClientGroupActivityRanking,
  generateReportByClosedJobs,
  getReportColumnConfig,
  saveReportColumnConfig,
  getDevWorklog,
} from "../api";
import { useAuth } from "../context/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm"; // Import the plugin
import { registerLocale, setDefaultLocale } from "react-datepicker";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enAU } from "date-fns/locale/en-AU";
import { format, set } from "date-fns";
import { getDateRangeOrderError } from "../utils/dateRangeOrder";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { DataTable } from "../components/DataTable";
import type { ColumnConfig, SortDirection } from "../components/DataTable";
import {
  githubLinksFromRow,
  parseGithubLinksFromRaw,
  toGithubHref,
} from "../utils/githubLinks";
import { completionStatusLabel } from "../utils/completionStatus";
import { Icon } from "@iconify/react";
import DevWorklogReport from "../components/admin/DevWorklogReport";
import FinancialYearField from "../components/FinancialYearField";
// styles moved to global App.css
// Australian locale from date-fns

// Register the Australian locale
registerLocale("en-AU", enAU);
setDefaultLocale("en-AU");

/** Bootstrap appends backdrops as direct children of `body`. Do not remove in-app overlays under `#root`. */
function removeBootstrapBodyBackdrops() {
  if (typeof document === "undefined") return;
  document.querySelectorAll("body > .modal-backdrop").forEach((el) => el.remove());
}

/**
 * Nested modals can leave an extra `body > .modal-backdrop` after the top one closes.
 * Trim only surplus backdrops so a parent modal keeps its dimmer.
 */
function trimExcessModalBackdrops() {
  if (typeof document === "undefined") return;
  const openCount = document.querySelectorAll(".modal.show").length;
  let backs = document.querySelectorAll("body > .modal-backdrop");
  while (backs.length > openCount) {
    backs[backs.length - 1]?.remove();
    backs = document.querySelectorAll("body > .modal-backdrop");
  }
}

/**
 * Bootstrap 5 does not fully support stacked modals; closing a child opened from
 * Generate Report can leave no `.show` parent but a stray body backdrop. Trim and
 * reopen `#exampleModalToggle3` when needed.
 */
function afterNestedReportChildModalHidden() {
  if (typeof document === "undefined") return;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      trimExcessModalBackdrops();
      const genEl = document.getElementById("exampleModalToggle3");
      if (genEl && !genEl.classList.contains("show")) {
        Modal.getOrCreateInstance(genEl).show();
      }
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => trimExcessModalBackdrops());
      });
    });
  });
}

const DEV_WORKLOG_USER_ID = "daniyal";

/** When every Bootstrap modal is closed, drop stray backdrops / body scroll lock. */
function clearStaleBootstrapModalChrome() {
  if (typeof document === "undefined") return;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const openCount = document.querySelectorAll(".modal.show").length;
      if (openCount === 0) {
        document.querySelectorAll(".modal").forEach((el) => {
          el.classList.remove("show");
          (el as HTMLElement).style.removeProperty("display");
          el.setAttribute("aria-hidden", "true");
          el.removeAttribute("aria-modal");
        });
        removeBootstrapBodyBackdrops();
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("padding-left");
        document.body.style.removeProperty("overflow");
      } else {
        trimExcessModalBackdrops();
      }
    });
  });
}

/** Hard reset before programmatic open (fixes stuck backdrop on re-open). */
function forceBootstrapModalCleanup() {
  if (typeof document === "undefined") return;
  document.querySelectorAll(".modal").forEach((el) => {
    const inst = Modal.getInstance(el);
    inst?.dispose();
    el.classList.remove("show");
    (el as HTMLElement).style.removeProperty("display");
    el.setAttribute("aria-hidden", "true");
    el.removeAttribute("aria-modal");
  });
  removeBootstrapBodyBackdrops();
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("padding-right");
  document.body.style.removeProperty("padding-left");
  document.body.style.removeProperty("overflow");
}

/** After an AI child modal closes, reopen the hub on the next frames so Bootstrap backdrops stay in sync. */
function reopenAiReportHubModal() {
  if (typeof document === "undefined") return;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      trimExcessModalBackdrops();
      const hub = document.getElementById("aiReportHubModal");
      if (hub) {
        Modal.getOrCreateInstance(hub).show();
      }
      window.requestAnimationFrame(() => {
        trimExcessModalBackdrops();
      });
    });
  });
}

type UploadFilesModalId =
  | "temporalActivityUploadModal"
  | "activityResultUploadModal";

const UPLOAD_SHEETS_MODAL_OPTS: Partial<Modal.Options> = {
  backdrop: false,
  keyboard: true,
};

function getUploadSheetsModalInstance(modalId: UploadFilesModalId) {
  const el = document.getElementById(modalId);
  if (!el) return null;
  return Modal.getOrCreateInstance(el, UPLOAD_SHEETS_MODAL_OPTS);
}

/** Reopen an upload-files stack modal (custom backdrop — no Bootstrap backdrop). */
function reopenUploadFilesModal(modalId: UploadFilesModalId) {
  if (typeof document === "undefined") return;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      removeBootstrapBodyBackdrops();
      getUploadSheetsModalInstance(modalId)?.show();
    });
  });
}

// Helper to tidy up markdown
function tidyMarkdown(md: string): string {
  if (!md) return "";
  // Remove excessive blank lines, trim, normalize line endings, etc.
  return md
    .replace(/\r\n|\r/g, "\n") // Normalize newlines
    .replace(/\n{3,}/g, "\n\n") // No more than 2 consecutive newlines
    .replace(/[ \t]+$/gm, "") // Remove trailing spaces
    .trim();
}

/** Truncated first URL in admin job report tables; +N when more links exist. */
function renderAdminGithubTableCell(row: unknown) {
  const links = githubLinksFromRow(row);
  if (links.length === 0) return "—";
  const first = links[0];
  const extra = links.length - 1;
  return (
    <div
      className="d-flex align-items-center gap-1 admin-report-github-cell"
      style={{ minWidth: 0, maxWidth: 240 }}
      onClick={(e) => e.stopPropagation()}
    >
      <a
        href={toGithubHref(first)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-truncate"
        style={{ minWidth: 0, maxWidth: extra > 0 ? "10.5rem" : "14rem" }}
        title={first}
      >
        {first}
      </a>
      {extra > 0 ? (
        <span className="text-muted small text-nowrap flex-shrink-0">
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

/** Open jobs by client group API response row */
export interface OpenJobsByClientGroupRow {
  taskId: number;
  userId: string;
  jobName?: string;
  clientGroup: string;
  projectId: string;
  projectDesc: string;
  timeHrs: number;
  timeMin: number;
  estimatedHours: number;
  hoursAddedThisPeriod: number;
  totalLogHours: number;
  endDate: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  status: string;
  statusBoolean: boolean;
  notes: string | null;
  teamMembers: string | null;
  githubLink?: string | null;
  activityType: string | null;
  details: string | null;
  periodType: string | null;
  financialYear: string | null;
  financialPeriod: string | null;
}

const OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS: ColumnConfig<OpenJobsByClientGroupRow>[] =
  [
    {
      key: "clientGroup",
      label: "Client Group",
      minWidth: 140,
      sortable: true,
    },
    { key: "projectId", label: "Project ID", minWidth: 160, sortable: true },
    {
      key: "projectDesc",
      label: "Project Description",
      minWidth: 200,
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      minWidth: 100,
      sortable: true,
      render: (_v, row) => {
        const label = completionStatusLabel(row.status, row.statusBoolean);
        const cls =
          label === "—"
            ? "text-bg-secondary"
            : label === "Completed"
              ? "text-bg-success"
              : "text-bg-warning text-dark";
        return (
          <span className={`badge rounded-pill ${cls}`}>{label}</span>
        );
      },
    },
    {
      key: "teamMembers",
      label: "Team Members",
      minWidth: 140,
      sortable: false,
    },
    {
      key: "githubLink",
      label: "GitHub link",
      minWidth: 140,
      sortable: false,
      render: (_v, row) => renderAdminGithubTableCell(row),
    },
    {
      key: "estimatedHours",
      label: "Est. Hours",
      minWidth: 100,
      align: "right",
      sortable: true,
    },
    {
      key: "totalLogHours",
      label: "Total Logged",
      minWidth: 110,
      align: "right",
      sortable: true,
    },
    {
      key: "hoursAddedThisPeriod",
      label: "Hours This Period",
      minWidth: 130,
      align: "right",
      sortable: true,
    },
    { key: "endDate", label: "End Date", minWidth: 100, sortable: true },
    {
      key: "financialYear",
      label: "Financial Year",
      minWidth: 110,
      sortable: true,
    },
    { key: "notes", label: "Notes", minWidth: 120, sortable: false },
  ];

const REPORT_KEY_OPEN_JOBS_BY_CLIENT_GROUP = "open_jobs_by_client_group";
const REPORT_KEY_OPEN_JOBS_BY_JOB_NAME = "open_jobs_by_job_name";
const REPORT_KEY_CLOSED_JOBS_BY_CLIENT_GROUP = "closed_jobs_by_client_group";
const REPORT_KEY_CLOSED_JOBS_BY_JOB_NAME = "closed_jobs_by_job_name";

/** Map saved API column keys to visibility; unknown keys ignored; needs ≥1 valid key. */
function visibleMapFromSavedColumnKeys(
  keys: string[],
  columnDefs: { key: string }[],
): Record<string, boolean> | null {
  const valid = new Set(columnDefs.map((c) => c.key));
  const picked = keys.filter((k) => valid.has(k));
  if (picked.length === 0) return null;
  return Object.fromEntries(
    columnDefs.map((c) => [c.key, picked.includes(c.key)]),
  );
}

/** Stacked column picker (same layout for all table reports). */
function ReportColumnPickerModal({
  open,
  titleId,
  loading,
  saveLoading,
  columnDefs,
  draft,
  onToggle,
  onClose,
  onSave,
}: {
  open: boolean;
  titleId: string;
  loading: boolean;
  saveLoading: boolean;
  columnDefs: ColumnConfig<any>[];
  draft: Record<string, boolean>;
  onToggle: (key: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  if (!open) return null;
  return (
    <>
      <div
        className="modal fade show d-block report-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ zIndex: 1060 }}
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content overflow-hidden">
            <div className="modal-header">
              <div>
                <h1 className="modal-title fs-5 mb-1" id={titleId}>
                  Table columns
                </h1>
                <p
                  className="mb-0 small opacity-90"
                  style={{ fontWeight: 400, fontSize: "0.875rem" }}
                >
                  Tap a tile to include or exclude that column. The main text is
                  the table header; the smaller line is the field key saved in
                  your preferences (e.g.{" "}
                  <span className="text-white text-opacity-75">
                    totalLogHours
                  </span>
                  ).
                </p>
              </div>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div
              className="modal-body pt-3"
              style={{ background: "#f8fafb" }}
            >
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="row g-3">
                  {columnDefs.map((col) => {
                    const selected = draft[col.key] !== false;
                    return (
                      <div key={col.key} className="col-12 col-md-4">
                        <button
                          type="button"
                          className="w-100 text-start border-0 shadow-sm"
                          onClick={() => onToggle(col.key)}
                          aria-pressed={selected}
                          style={{
                            borderRadius: "12px",
                            minHeight: "72px",
                            padding: "14px 16px",
                            cursor: "pointer",
                            transition:
                              "transform 0.15s ease, box-shadow 0.15s ease",
                            background: selected
                              ? "linear-gradient(135deg, #1e88e5 0%, #1565c0 55%, #0d47a1 100%)"
                              : "#ffffff",
                            color: selected ? "#ffffff" : "#2b6777",
                            border: selected
                              ? "1px solid transparent"
                              : "1px solid #d8e3ea",
                            boxShadow: selected
                              ? "0 6px 16px rgba(21, 101, 192, 0.35)"
                              : "0 1px 3px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="min-w-0 flex-grow-1">
                              <div
                                className="fw-semibold"
                                style={{
                                  fontSize: "0.95rem",
                                  lineHeight: 1.3,
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {col.label}
                              </div>
                              <div
                                className="mt-1 font-monospace"
                                style={{
                                  fontSize: "0.7rem",
                                  opacity: selected ? 0.85 : 0.55,
                                  wordBreak: "break-all",
                                }}
                              >
                                {col.key}
                              </div>
                            </div>
                            <div
                              className="flex-shrink-0 d-flex align-items-center justify-content-center"
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: "8px",
                                background: selected
                                  ? "rgba(255,255,255,0.22)"
                                  : "#eef4f7",
                                border: selected
                                  ? "none"
                                  : "1px solid #d8e3ea",
                                fontSize: "0.95rem",
                                fontWeight: 700,
                                color: selected ? "#fff" : "#90a4ae",
                              }}
                              aria-hidden
                            >
                              {selected ? "✓" : ""}
                            </div>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer d-flex gap-2 justify-content-end flex-wrap">
              <button
                type="button"
                className="cancel-button"
                style={{ width: "auto" }}
                onClick={onClose}
                disabled={saveLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="form-button"
                style={{ width: "auto", minWidth: "120px" }}
                onClick={() => void onSave()}
                disabled={loading || saveLoading}
              >
                {saveLoading ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1056 }} />
    </>
  );
}

/** Open jobs by job name API response row */
export interface OpenJobsByJobNameRow {
  taskId: number;
  userId: string;
  jobName: string;
  clientGroup: string;
  projectId: string;
  projectDesc: string;
  timeHrs: number;
  timeMin: number;
  estimatedHours: number;
  hoursAddedThisPeriod: number;
  totalLogHours: number;
  endDate: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  status: string;
  statusBoolean: boolean;
  notes: string | null;
  teamMembers: string | null;
  githubLink?: string | null;
  activityType: string | null;
  details: string | null;
  periodType: string | null;
  financialYear: string | null;
  financialPeriod: string | null;
}

const OPEN_JOBS_BY_JOB_NAME_COLUMNS: ColumnConfig<OpenJobsByJobNameRow>[] = [
  {
    key: "jobName",
    label: "Name",
    minWidth: 160,
    sortable: true,
    sortKey: "projectId",
    render: (_value, row) => row.projectId ?? "",
  },
  { key: "clientGroup", label: "Client Group", minWidth: 140, sortable: true },
  {
    key: "projectDesc",
    label: "Project Description",
    minWidth: 200,
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    minWidth: 100,
    sortable: true,
    render: (_v, row) => {
      const label = completionStatusLabel(row.status, row.statusBoolean);
      const cls =
        label === "—"
          ? "text-bg-secondary"
          : label === "Completed"
            ? "text-bg-success"
            : "text-bg-warning text-dark";
      return <span className={`badge rounded-pill ${cls}`}>{label}</span>;
    },
  },
  { key: "teamMembers", label: "Team Members", minWidth: 140, sortable: false },
  {
    key: "githubLink",
    label: "GitHub link",
    minWidth: 140,
    sortable: false,
    render: (_v, row) => renderAdminGithubTableCell(row),
  },
  {
    key: "estimatedHours",
    label: "Est. Hours",
    minWidth: 100,
    align: "right",
    sortable: true,
  },
  {
    key: "totalLogHours",
    label: "Total Logged",
    minWidth: 110,
    align: "right",
    sortable: true,
  },
  {
    key: "hoursAddedThisPeriod",
    label: "Hours This Period",
    minWidth: 130,
    align: "right",
    sortable: true,
  },
  { key: "endDate", label: "End Date", minWidth: 100, sortable: true },
  {
    key: "financialYear",
    label: "Financial Year",
    minWidth: 110,
    sortable: true,
  },
  { key: "notes", label: "Notes", minWidth: 120, sortable: false },
];

/** Closed jobs API response row (same shape for ClientGroup true/false) */
export interface ClosedJobsRow {
  taskId: number;
  userId: string;
  jobName: string;
  clientGroup: string;
  projectId: string;
  projectDesc: string;
  timeHrs: number;
  timeMin: number;
  estimatedHours: number;
  hoursAddedThisPeriod: number;
  totalLogHours: number;
  endDate: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  status: string;
  statusBoolean: boolean;
  notes: string | null;
  teamMembers: string | null;
  githubLink?: string | null;
  activityType: string | null;
  details: string | null;
  periodType: string | null;
  financialYear: string | null;
  financialPeriod: string | null;
}

type ReportViewMode = "table" | "cards";

interface GroupedClientJobs<T> {
  totalHoursLoggedThisPeriodByClientGroup: number;
  jobs: T[];
}

type GroupedClientJobsResponse<T> = Record<string, GroupedClientJobs<T>>;

interface ClientGroupHoursUser {
  userId: string;
  name: string;
}

interface ClientGroupHoursJob {
  taskId: number;
  projectId: string | null;
  projectDesc: string | null;
  status: "Open" | "Closed";
  userHours: Record<string, number>;
  totalHours: number;
}

interface ClientGroupHoursGroup {
  clientGroup: string;
  jobs: ClientGroupHoursJob[];
  userHours: Record<string, number>;
  totalHours: number;
}

interface ClientGroupHoursResponse {
  data: ClientGroupHoursGroup[];
  users: ClientGroupHoursUser[];
  totalJobs: number;
  closed: boolean;
}

const CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS: ColumnConfig<ClosedJobsRow>[] = [
  { key: "clientGroup", label: "Client Group", minWidth: 140, sortable: true },
  {
    key: "jobName",
    label: "Name",
    minWidth: 160,
    sortable: true,
    sortKey: "projectId",
    render: (_value, row) => row.projectId ?? "",
  },
  {
    key: "projectDesc",
    label: "Project Description",
    minWidth: 200,
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    minWidth: 100,
    sortable: true,
    render: (_v, row) => {
      const label = completionStatusLabel(row.status, row.statusBoolean);
      const cls =
        label === "—"
          ? "text-bg-secondary"
          : label === "Completed"
            ? "text-bg-success"
            : "text-bg-warning text-dark";
      return <span className={`badge rounded-pill ${cls}`}>{label}</span>;
    },
  },
  { key: "teamMembers", label: "Team Members", minWidth: 140, sortable: false },
  {
    key: "githubLink",
    label: "GitHub link",
    minWidth: 140,
    sortable: false,
    render: (_v, row) => renderAdminGithubTableCell(row),
  },
  {
    key: "estimatedHours",
    label: "Est. Hours",
    minWidth: 100,
    align: "right",
    sortable: true,
  },
  {
    key: "totalLogHours",
    label: "Total Logged",
    minWidth: 110,
    align: "right",
    sortable: true,
  },
  {
    key: "hoursAddedThisPeriod",
    label: "Hours This Period",
    minWidth: 130,
    align: "right",
    sortable: true,
  },
  { key: "endDate", label: "End Date", minWidth: 100, sortable: true },
  {
    key: "financialYear",
    label: "Financial Year",
    minWidth: 110,
    sortable: true,
  },
  { key: "notes", label: "Notes", minWidth: 120, sortable: false },
];

const CLOSED_JOBS_BY_JOB_NAME_COLUMNS: ColumnConfig<ClosedJobsRow>[] = [
  {
    key: "jobName",
    label: "Name",
    minWidth: 160,
    sortable: true,
    sortKey: "projectId",
    render: (_value, row) => row.projectId ?? "",
  },
  { key: "clientGroup", label: "Client Group", minWidth: 140, sortable: true },
  {
    key: "projectDesc",
    label: "Project Description",
    minWidth: 200,
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    minWidth: 100,
    sortable: true,
    render: (_v, row) => {
      const label = completionStatusLabel(row.status, row.statusBoolean);
      const cls =
        label === "—"
          ? "text-bg-secondary"
          : label === "Completed"
            ? "text-bg-success"
            : "text-bg-warning text-dark";
      return <span className={`badge rounded-pill ${cls}`}>{label}</span>;
    },
  },
  { key: "teamMembers", label: "Team Members", minWidth: 140, sortable: false },
  {
    key: "githubLink",
    label: "GitHub link",
    minWidth: 140,
    sortable: false,
    render: (_v, row) => renderAdminGithubTableCell(row),
  },
  {
    key: "estimatedHours",
    label: "Est. Hours",
    minWidth: 100,
    align: "right",
    sortable: true,
  },
  {
    key: "totalLogHours",
    label: "Total Logged",
    minWidth: 110,
    align: "right",
    sortable: true,
  },
  {
    key: "hoursAddedThisPeriod",
    label: "Hours This Period",
    minWidth: 130,
    align: "right",
    sortable: true,
  },
  { key: "endDate", label: "End Date", minWidth: 100, sortable: true },
  {
    key: "financialYear",
    label: "Financial Year",
    minWidth: 110,
    sortable: true,
  },
  { key: "notes", label: "Notes", minWidth: 120, sortable: false },
];

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    tabular_view: "normal",
    startDate: "",
    endDate: "",
    search: "",
  });

  const [currentReportType, setCurrentReportType] = useState<string>("");
  const [clientGroupHoursClosed, setClientGroupHoursClosed] = useState(false);
  const [clientGroupHoursData, setClientGroupHoursData] =
    useState<ClientGroupHoursResponse | null>(null);
  const [clientGroupHoursLoading, setClientGroupHoursLoading] = useState(false);
  /** Row opened from admin job reports (table click) for full-field view including GitHub URLs. */
  const [reportJobDetailRow, setReportJobDetailRow] = useState<
    Record<string, unknown> | null
  >(null);

  // Open jobs by client group: table data and sort (API-driven)
  const [openJobsTableData, setOpenJobsTableData] = useState<
    OpenJobsByClientGroupRow[] | null
  >(null);
  const [openJobsByClientGroupViewMode, setOpenJobsByClientGroupViewMode] =
    useState<ReportViewMode>("table");
  const [openJobsGroupedCardData, setOpenJobsGroupedCardData] =
    useState<GroupedClientJobsResponse<OpenJobsByClientGroupRow> | null>(null);
  const [openJobsExpandedGroups, setOpenJobsExpandedGroups] = useState<
    Record<string, boolean>
  >({});
  const [openJobsLoading, setOpenJobsLoading] = useState(false);
  const [openJobsSortColumn, setOpenJobsSortColumn] = useState<string | null>(
    "clientGroup",
  );
  const [openJobsSortDirection, setOpenJobsSortDirection] =
    useState<SortDirection>("asc");
  // Column visibility: key -> visible (default all true)
  const [openJobsVisibleColumns, setOpenJobsVisibleColumns] = useState<
    Record<string, boolean>
  >(() =>
    Object.fromEntries(
      OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS.map((c) => [c.key, true]),
    ),
  );

  const openJobsColumnsVisible = OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS.filter(
    (col) => openJobsVisibleColumns[col.key] !== false,
  );

  const [openJobsColumnPickerOpen, setOpenJobsColumnPickerOpen] =
    useState(false);
  const [openJobsColumnPickerDraft, setOpenJobsColumnPickerDraft] = useState<
    Record<string, boolean>
  >(() =>
    Object.fromEntries(
      OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS.map((c) => [c.key, true]),
    ),
  );
  const [openJobsColumnPickerLoading, setOpenJobsColumnPickerLoading] =
    useState(false);
  const [openJobsColumnSaveLoading, setOpenJobsColumnSaveLoading] =
    useState(false);

  const toggleOpenJobsColumnPickerDraft = (key: string) => {
    setOpenJobsColumnPickerDraft((prev) => {
      const visibleCount = OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS.filter(
        (c) => prev[c.key] !== false,
      ).length;
      const currentlyVisible = prev[key] !== false;
      if (currentlyVisible && visibleCount <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const loadOpenJobsColumnConfigFromApi = async () => {
    const uid = user?.user_id;
    if (!uid) return;
    try {
      const res: any = await getReportColumnConfig(
        uid,
        REPORT_KEY_OPEN_JOBS_BY_CLIENT_GROUP,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS,
        );
        if (m) setOpenJobsVisibleColumns(m);
      }
    } catch (e) {
      console.error("Failed to load open jobs column config:", e);
    }
  };

  const handleOpenOpenJobsColumnPicker = async () => {
    if (!user?.user_id) {
      setError("You must be logged in to manage column visibility.");
      return;
    }
    setOpenJobsColumnPickerOpen(true);
    setOpenJobsColumnPickerLoading(true);
    setOpenJobsColumnPickerDraft({ ...openJobsVisibleColumns });
    try {
      const res: any = await getReportColumnConfig(
        user.user_id,
        REPORT_KEY_OPEN_JOBS_BY_CLIENT_GROUP,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS,
        );
        if (m) setOpenJobsColumnPickerDraft(m);
      }
    } catch (e) {
      console.error("Failed to load column config for picker:", e);
      setOpenJobsColumnPickerDraft({ ...openJobsVisibleColumns });
    } finally {
      setOpenJobsColumnPickerLoading(false);
    }
  };

  const handleCloseOpenJobsColumnPicker = () => {
    setOpenJobsColumnPickerOpen(false);
  };

  const handleSaveOpenJobsColumnConfig = async () => {
    if (!user?.user_id) return;
    const keys = OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS.filter(
      (c) => openJobsColumnPickerDraft[c.key] !== false,
    ).map((c) => c.key);
    if (keys.length === 0) return;
    setOpenJobsColumnSaveLoading(true);
    try {
      await saveReportColumnConfig({
        userId: user.user_id,
        reportKey: REPORT_KEY_OPEN_JOBS_BY_CLIENT_GROUP,
        columns: keys,
      });
      setOpenJobsVisibleColumns({ ...openJobsColumnPickerDraft });
      setOpenJobsColumnPickerOpen(false);
    } catch (e) {
      console.error("Failed to save column config:", e);
      setError("Could not save column settings. Please try again.");
    } finally {
      setOpenJobsColumnSaveLoading(false);
    }
  };

  // Open jobs by job name: table data and sort (API-driven)
  const [openJobsByJobNameTableData, setOpenJobsByJobNameTableData] = useState<
    OpenJobsByJobNameRow[] | null
  >(null);
  const [openJobsByJobNameLoading, setOpenJobsByJobNameLoading] =
    useState(false);
  const [openJobsByJobNameSortColumn, setOpenJobsByJobNameSortColumn] =
    useState<string | null>("projectId");
  const [openJobsByJobNameSortDirection, setOpenJobsByJobNameSortDirection] =
    useState<SortDirection>("asc");
  const [openJobsByJobNameVisibleColumns, setOpenJobsByJobNameVisibleColumns] =
    useState<Record<string, boolean>>(() =>
      Object.fromEntries(
        OPEN_JOBS_BY_JOB_NAME_COLUMNS.map((c) => [c.key, true]),
      ),
    );
  const openJobsByJobNameColumnsVisible = OPEN_JOBS_BY_JOB_NAME_COLUMNS.filter(
    (col) => openJobsByJobNameVisibleColumns[col.key] !== false,
  );

  const [openJobsByJobNameColumnPickerOpen, setOpenJobsByJobNameColumnPickerOpen] =
    useState(false);
  const [openJobsByJobNameColumnPickerDraft, setOpenJobsByJobNameColumnPickerDraft] =
    useState<Record<string, boolean>>(() =>
      Object.fromEntries(
        OPEN_JOBS_BY_JOB_NAME_COLUMNS.map((c) => [c.key, true]),
      ),
    );
  const [openJobsByJobNameColumnPickerLoading, setOpenJobsByJobNameColumnPickerLoading] =
    useState(false);
  const [openJobsByJobNameColumnSaveLoading, setOpenJobsByJobNameColumnSaveLoading] =
    useState(false);

  const toggleOpenJobsByJobNameColumnPickerDraft = (key: string) => {
    setOpenJobsByJobNameColumnPickerDraft((prev) => {
      const visibleCount = OPEN_JOBS_BY_JOB_NAME_COLUMNS.filter(
        (c) => prev[c.key] !== false,
      ).length;
      const currentlyVisible = prev[key] !== false;
      if (currentlyVisible && visibleCount <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const loadOpenJobsByJobNameColumnConfigFromApi = async () => {
    const uid = user?.user_id;
    if (!uid) return;
    try {
      const res: any = await getReportColumnConfig(
        uid,
        REPORT_KEY_OPEN_JOBS_BY_JOB_NAME,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          OPEN_JOBS_BY_JOB_NAME_COLUMNS,
        );
        if (m) setOpenJobsByJobNameVisibleColumns(m);
      }
    } catch (e) {
      console.error("Failed to load open jobs by job name column config:", e);
    }
  };

  const handleOpenOpenJobsByJobNameColumnPicker = async () => {
    if (!user?.user_id) {
      setError("You must be logged in to manage column visibility.");
      return;
    }
    setOpenJobsByJobNameColumnPickerOpen(true);
    setOpenJobsByJobNameColumnPickerLoading(true);
    setOpenJobsByJobNameColumnPickerDraft({
      ...openJobsByJobNameVisibleColumns,
    });
    try {
      const res: any = await getReportColumnConfig(
        user.user_id,
        REPORT_KEY_OPEN_JOBS_BY_JOB_NAME,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          OPEN_JOBS_BY_JOB_NAME_COLUMNS,
        );
        if (m) setOpenJobsByJobNameColumnPickerDraft(m);
      }
    } catch (e) {
      console.error(e);
      setOpenJobsByJobNameColumnPickerDraft({
        ...openJobsByJobNameVisibleColumns,
      });
    } finally {
      setOpenJobsByJobNameColumnPickerLoading(false);
    }
  };

  const handleCloseOpenJobsByJobNameColumnPicker = () => {
    setOpenJobsByJobNameColumnPickerOpen(false);
  };

  const handleSaveOpenJobsByJobNameColumnConfig = async () => {
    if (!user?.user_id) return;
    const keys = OPEN_JOBS_BY_JOB_NAME_COLUMNS.filter(
      (c) => openJobsByJobNameColumnPickerDraft[c.key] !== false,
    ).map((c) => c.key);
    if (keys.length === 0) return;
    setOpenJobsByJobNameColumnSaveLoading(true);
    try {
      await saveReportColumnConfig({
        userId: user.user_id,
        reportKey: REPORT_KEY_OPEN_JOBS_BY_JOB_NAME,
        columns: keys,
      });
      setOpenJobsByJobNameVisibleColumns({
        ...openJobsByJobNameColumnPickerDraft,
      });
      setOpenJobsByJobNameColumnPickerOpen(false);
    } catch (e) {
      console.error(e);
      setError("Could not save column settings. Please try again.");
    } finally {
      setOpenJobsByJobNameColumnSaveLoading(false);
    }
  };

  // Closed jobs by client group: table data and sort (API-driven)
  const [
    closedJobsByClientGroupTableData,
    setClosedJobsByClientGroupTableData,
  ] = useState<ClosedJobsRow[] | null>(null);
  const [closedJobsByClientGroupViewMode, setClosedJobsByClientGroupViewMode] =
    useState<ReportViewMode>("table");
  const [
    closedJobsByClientGroupGroupedCardData,
    setClosedJobsByClientGroupGroupedCardData,
  ] = useState<GroupedClientJobsResponse<ClosedJobsRow> | null>(null);
  const [closedJobsExpandedGroups, setClosedJobsExpandedGroups] = useState<
    Record<string, boolean>
  >({});
  const [closedJobsByClientGroupLoading, setClosedJobsByClientGroupLoading] =
    useState(false);
  const [
    closedJobsByClientGroupSortColumn,
    setClosedJobsByClientGroupSortColumn,
  ] = useState<string | null>("clientGroup");
  const [
    closedJobsByClientGroupSortDirection,
    setClosedJobsByClientGroupSortDirection,
  ] = useState<SortDirection>("asc");
  const [
    closedJobsByClientGroupVisibleColumns,
    setClosedJobsByClientGroupVisibleColumns,
  ] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS.map((c) => [c.key, true]),
    ),
  );
  const closedJobsByClientGroupColumnsVisible =
    CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS.filter(
      (col) => closedJobsByClientGroupVisibleColumns[col.key] !== false,
    );

  // Closed jobs by client group / by job name: separate report keys & saved configs
  const [
    closedJobsByClientGroupColumnPickerOpen,
    setClosedJobsByClientGroupColumnPickerOpen,
  ] = useState(false);
  const [
    closedJobsByJobNameColumnPickerOpen,
    setClosedJobsByJobNameColumnPickerOpen,
  ] = useState(false);
  const [
    closedJobsByClientGroupColumnPickerDraft,
    setClosedJobsByClientGroupColumnPickerDraft,
  ] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS.map((c) => [c.key, true]),
    ),
  );
  const [
    closedJobsByJobNameColumnPickerDraft,
    setClosedJobsByJobNameColumnPickerDraft,
  ] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      CLOSED_JOBS_BY_JOB_NAME_COLUMNS.map((c) => [c.key, true]),
    ),
  );
  const [
    closedJobsByClientGroupColumnPickerLoading,
    setClosedJobsByClientGroupColumnPickerLoading,
  ] = useState(false);
  const [
    closedJobsByClientGroupColumnSaveLoading,
    setClosedJobsByClientGroupColumnSaveLoading,
  ] = useState(false);
  const [
    closedJobsByJobNameColumnPickerLoading,
    setClosedJobsByJobNameColumnPickerLoading,
  ] = useState(false);
  const [
    closedJobsByJobNameColumnSaveLoading,
    setClosedJobsByJobNameColumnSaveLoading,
  ] = useState(false);

  const toggleClosedJobsByClientGroupColumnPickerDraft = (key: string) => {
    setClosedJobsByClientGroupColumnPickerDraft((prev) => {
      const visibleCount = CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS.filter(
        (c) => prev[c.key] !== false,
      ).length;
      if (prev[key] !== false && visibleCount <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const toggleClosedJobsByJobNameColumnPickerDraft = (key: string) => {
    setClosedJobsByJobNameColumnPickerDraft((prev) => {
      const visibleCount = CLOSED_JOBS_BY_JOB_NAME_COLUMNS.filter(
        (c) => prev[c.key] !== false,
      ).length;
      if (prev[key] !== false && visibleCount <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const loadClosedJobsByClientGroupColumnConfigFromApi = async () => {
    const uid = user?.user_id;
    if (!uid) return;
    try {
      const res: any = await getReportColumnConfig(
        uid,
        REPORT_KEY_CLOSED_JOBS_BY_CLIENT_GROUP,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS,
        );
        if (m) setClosedJobsByClientGroupVisibleColumns(m);
      }
    } catch (e) {
      console.error(
        "Failed to load closed jobs by client group column config:",
        e,
      );
    }
  };

  const loadClosedJobsByJobNameColumnConfigFromApi = async () => {
    const uid = user?.user_id;
    if (!uid) return;
    try {
      const res: any = await getReportColumnConfig(
        uid,
        REPORT_KEY_CLOSED_JOBS_BY_JOB_NAME,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          CLOSED_JOBS_BY_JOB_NAME_COLUMNS,
        );
        if (m) setClosedJobsByJobNameVisibleColumns(m);
      }
    } catch (e) {
      console.error(
        "Failed to load closed jobs by job name column config:",
        e,
      );
    }
  };

  const handleOpenClosedJobsByClientGroupColumnPicker = async () => {
    if (!user?.user_id) {
      setError("You must be logged in to manage column visibility.");
      return;
    }
    setClosedJobsByClientGroupColumnPickerOpen(true);
    setClosedJobsByClientGroupColumnPickerLoading(true);
    setClosedJobsByClientGroupColumnPickerDraft({
      ...closedJobsByClientGroupVisibleColumns,
    });
    try {
      const res: any = await getReportColumnConfig(
        user.user_id,
        REPORT_KEY_CLOSED_JOBS_BY_CLIENT_GROUP,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS,
        );
        if (m) setClosedJobsByClientGroupColumnPickerDraft(m);
      }
    } catch (e) {
      console.error(e);
      setClosedJobsByClientGroupColumnPickerDraft({
        ...closedJobsByClientGroupVisibleColumns,
      });
    } finally {
      setClosedJobsByClientGroupColumnPickerLoading(false);
    }
  };

  const handleOpenClosedJobsByJobNameColumnPicker = async () => {
    if (!user?.user_id) {
      setError("You must be logged in to manage column visibility.");
      return;
    }
    setClosedJobsByJobNameColumnPickerOpen(true);
    setClosedJobsByJobNameColumnPickerLoading(true);
    setClosedJobsByJobNameColumnPickerDraft({
      ...closedJobsByJobNameVisibleColumns,
    });
    try {
      const res: any = await getReportColumnConfig(
        user.user_id,
        REPORT_KEY_CLOSED_JOBS_BY_JOB_NAME,
      );
      const data = res?.data?.data ?? res?.data;
      const columns = data?.columns;
      if (Array.isArray(columns) && columns.length > 0) {
        const m = visibleMapFromSavedColumnKeys(
          columns,
          CLOSED_JOBS_BY_JOB_NAME_COLUMNS,
        );
        if (m) setClosedJobsByJobNameColumnPickerDraft(m);
      }
    } catch (e) {
      console.error(e);
      setClosedJobsByJobNameColumnPickerDraft({
        ...closedJobsByJobNameVisibleColumns,
      });
    } finally {
      setClosedJobsByJobNameColumnPickerLoading(false);
    }
  };

  const handleCloseClosedJobsByClientGroupColumnPicker = () => {
    setClosedJobsByClientGroupColumnPickerOpen(false);
  };

  const handleCloseClosedJobsByJobNameColumnPicker = () => {
    setClosedJobsByJobNameColumnPickerOpen(false);
  };

  const handleSaveClosedJobsByClientGroupColumnConfig = async () => {
    if (!user?.user_id) return;
    const keys = CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS.filter(
      (c) => closedJobsByClientGroupColumnPickerDraft[c.key] !== false,
    ).map((c) => c.key);
    if (keys.length === 0) return;
    setClosedJobsByClientGroupColumnSaveLoading(true);
    try {
      await saveReportColumnConfig({
        userId: user.user_id,
        reportKey: REPORT_KEY_CLOSED_JOBS_BY_CLIENT_GROUP,
        columns: keys,
      });
      setClosedJobsByClientGroupVisibleColumns({
        ...closedJobsByClientGroupColumnPickerDraft,
      });
      setClosedJobsByClientGroupColumnPickerOpen(false);
    } catch (e) {
      console.error(e);
      setError("Could not save column settings. Please try again.");
    } finally {
      setClosedJobsByClientGroupColumnSaveLoading(false);
    }
  };

  const handleSaveClosedJobsByJobNameColumnConfig = async () => {
    if (!user?.user_id) return;
    const keys = CLOSED_JOBS_BY_JOB_NAME_COLUMNS.filter(
      (c) => closedJobsByJobNameColumnPickerDraft[c.key] !== false,
    ).map((c) => c.key);
    if (keys.length === 0) return;
    setClosedJobsByJobNameColumnSaveLoading(true);
    try {
      await saveReportColumnConfig({
        userId: user.user_id,
        reportKey: REPORT_KEY_CLOSED_JOBS_BY_JOB_NAME,
        columns: keys,
      });
      setClosedJobsByJobNameVisibleColumns({
        ...closedJobsByJobNameColumnPickerDraft,
      });
      setClosedJobsByJobNameColumnPickerOpen(false);
    } catch (e) {
      console.error(e);
      setError("Could not save column settings. Please try again.");
    } finally {
      setClosedJobsByJobNameColumnSaveLoading(false);
    }
  };

  // Closed jobs by job name: table data and sort (API-driven)
  const [closedJobsByJobNameTableData, setClosedJobsByJobNameTableData] =
    useState<ClosedJobsRow[] | null>(null);
  const [closedJobsByJobNameLoading, setClosedJobsByJobNameLoading] =
    useState(false);
  const [closedJobsByJobNameSortColumn, setClosedJobsByJobNameSortColumn] =
    useState<string | null>("projectId");
  const [
    closedJobsByJobNameSortDirection,
    setClosedJobsByJobNameSortDirection,
  ] = useState<SortDirection>("asc");
  const [
    closedJobsByJobNameVisibleColumns,
    setClosedJobsByJobNameVisibleColumns,
  ] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      CLOSED_JOBS_BY_JOB_NAME_COLUMNS.map((c) => [c.key, true]),
    ),
  );
  const closedJobsByJobNameColumnsVisible =
    CLOSED_JOBS_BY_JOB_NAME_COLUMNS.filter(
      (col) => closedJobsByJobNameVisibleColumns[col.key] !== false,
    );

  const [userData, setUserData] = useState({
    name: "",
    user_id: "",
    password: "",
    isAdmin: false,
    endDate: new Date(),
    startDate: new Date(),
    periodType: "",
    financialYear: "",
    financialPeriod: "",
    periodStartDate: new Date(),
    periodEndDate: new Date(),
    taskName: "",
    byTaskStartDate: new Date(),
    byTaskEndDate: new Date(),
    clientGroupName: "",
    byClientGroupStartDate: new Date(),
    byClientGroupEndDate: new Date(),
  });

  const [users, setUsers] = useState<any>();

  const [user_id, setUserId] = useState("");

  const [selectedUser, setSelectedUser] = useState("");

  const [report, setReport] = useState<any>("");

  const [allReport, setAllReports] = useState<any>([]);

  const [newMessage, setNewMessage] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<any>([]); // To store query-response pairs
  const [selectedTask, setSelectedTask] = useState("");
  const [selectedClientGroup, setSelectedClientGroup] = useState("");
  const [tasks, setTasks] = useState<any>([]);
  const [clientGroups, setClientGroups] = useState<any>([]);
  const [sheet1, setSheet1] = useState<any>(null);
  const [sheet2, setSheet2] = useState<any>(null);
  const [selectedActivity, setSelectedActivity] = useState("");
  const [trackingData, setTrackingData] = useState<any>([]);
  const [uploadResults, setUploadResults] = useState<any>("");
  const [trackingDataLoading, setTrackingDataLoading] = useState(false);
  const [uploadDataLoading, setUploadDataLoading] = useState(false);
  const [uploadSheetsBlurOpen, setUploadSheetsBlurOpen] = useState(false);
  const [AIReportLoading, setAIReportLoading] = useState(false);
  const [aiGithubProjectLoading, setAiGithubProjectLoading] = useState(false);
  const [aiGithubProjectResult, setAiGithubProjectResult] = useState<any>(null);
  const [aiGithubProjectError, setAiGithubProjectError] = useState("");
  const [aiGithubProjectName, setAiGithubProjectName] = useState("");
  const [aiGithubProjectStart, setAiGithubProjectStart] = useState(new Date());
  const [aiGithubProjectEnd, setAiGithubProjectEnd] = useState(new Date());
  const [dashboardData, setDashboardData] = useState<any>([]);
  const [dashboardDays, setDashboardDays] = useState("20");
  const [availableDates, setAvailableDates] = useState<any>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<string>("");
  const [selectedEndDate, setSelectedEndDate] = useState<string>("");
  const [hasUserSelectedDates, setHasUserSelectedDates] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [selectedDashboardMarkdown, setSelectedDashboardMarkdown] =
    useState<string>("");
  const [showDashboardMarkdownModal, setShowDashboardMarkdownModal] =
    useState(false);
  const [worklogStartDate, setWorklogStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [worklogEndDate, setWorklogEndDate] = useState(() => new Date());
  const [worklogLoading, setWorklogLoading] = useState(false);
  const [worklogData, setWorklogData] = useState<unknown>(null);
  const [worklogError, setWorklogError] = useState("");

  /**
   * AI hub → child modal must open only after the hub has fully hidden.
   * Otherwise Bootstrap still counts the hub as `.modal.show` while the child
   * opens, which stacks extra `body > .modal-backdrop` nodes and blocks the UI
   * when the hub is opened again.
   */
  const aiHubOpeningChildRef = useRef<"employee" | "github" | null>(null);
  /** After hub → child flow, closing the child should reopen the hub (then user closes hub to leave). */
  const aiExpectHubAfterChildCloseRef = useRef(false);
  const uploadFilesOpeningChildRef = useRef<UploadFilesModalId | null>(null);
  const uploadExpectParentAfterChildCloseRef =
    useRef<UploadFilesModalId | null>(null);

  /** Same project list as "Report by task" (`getAllTasks` → `tasks`). Deduped, no blanks. */
  const githubProjectPickerOptions = useMemo(() => {
    const raw = Array.isArray(tasks) ? tasks : [];
    const names = raw
      .map((t: unknown) => String(t ?? "").trim())
      .filter((s) => s.length > 0);
    return Array.from(new Set(names)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [tasks]);

  // const TASKS=['Task1','Task2','Task3','Task4','TASK5','TASK6']
  // const CLIENT_GROUPS=['ClientGroup1','ClientGroup2','ClientGroup3','ClientGroup4','ClientGroup5','ClientGroup6']
  const ACTIVITIES = [
    "New → Draft",
    "New → Complete",
    "New → Out to Sign",
    "New → Signed",
    "New → Lodged",
    "New → Assessed",
    "Draft → Complete",
    "Draft → Out to Sign",
    "Draft → Signed",
    "Draft → Lodged",
    "Draft → Assessed",
    "Complete → Out to Sign",
    "Complete → Signed",
    "Complete → Lodged",
    "Complete → Assessed",
    "Out to Sign → Signed",
    "Out to Sign → Lodged",
    "Out to Sign → Assessed",
    "Signed → Lodged",
    "Signed → Assessed",
    "Lodged → Assessed",
  ];

  useEffect(() => {
    const aiEl = document.getElementById("exampleModalToggle6");
    const projEl = document.getElementById("aiReportGithubProjectModal");
    const onAiHidden = () => {
      if (aiExpectHubAfterChildCloseRef.current) {
        aiExpectHubAfterChildCloseRef.current = false;
        reopenAiReportHubModal();
        return;
      }
      clearStaleBootstrapModalChrome();
    };
    const onProjHidden = () => {
      setAiGithubProjectResult(null);
      setAiGithubProjectError("");
      setAiGithubProjectName("");
      setAiGithubProjectStart(new Date());
      setAiGithubProjectEnd(new Date());
      if (aiExpectHubAfterChildCloseRef.current) {
        aiExpectHubAfterChildCloseRef.current = false;
        reopenAiReportHubModal();
        return;
      }
      clearStaleBootstrapModalChrome();
    };
    aiEl?.addEventListener("hidden.bs.modal", onAiHidden);
    projEl?.addEventListener("hidden.bs.modal", onProjHidden);
    return () => {
      aiEl?.removeEventListener("hidden.bs.modal", onAiHidden);
      projEl?.removeEventListener("hidden.bs.modal", onProjHidden);
    };
  }, []);

  useEffect(() => {
    const hub = document.getElementById("aiReportHubModal");
    if (!hub) return;

    const onHubShown = () => {
      window.requestAnimationFrame(() => {
        trimExcessModalBackdrops();
      });
    };

    const onHubHidden = () => {
      const next = aiHubOpeningChildRef.current;
      aiHubOpeningChildRef.current = null;

      if (next === "employee") {
        const el = document.getElementById("exampleModalToggle6");
        if (el) {
          aiExpectHubAfterChildCloseRef.current = true;
          window.requestAnimationFrame(() => {
            Modal.getOrCreateInstance(el).show();
          });
        } else {
          clearStaleBootstrapModalChrome();
        }
        return;
      }

      if (next === "github") {
        setAiGithubProjectResult(null);
        setAiGithubProjectError("");
        setAiGithubProjectName("");
        setAiGithubProjectStart(new Date());
        setAiGithubProjectEnd(new Date());
        const el = document.getElementById("aiReportGithubProjectModal");
        if (el) {
          aiExpectHubAfterChildCloseRef.current = true;
          window.requestAnimationFrame(() => {
            Modal.getOrCreateInstance(el).show();
          });
        } else {
          clearStaleBootstrapModalChrome();
        }
        return;
      }

      clearStaleBootstrapModalChrome();
    };

    hub.addEventListener("shown.bs.modal", onHubShown);
    hub.addEventListener("hidden.bs.modal", onHubHidden);
    return () => {
      hub.removeEventListener("shown.bs.modal", onHubShown);
      hub.removeEventListener("hidden.bs.modal", onHubHidden);
    };
  }, []);

  useEffect(() => {
    const upload = document.getElementById("temporalActivityUploadModal");
    const results = document.getElementById("activityResultUploadModal");

    const onUploadHidden = () => {
      const next = uploadFilesOpeningChildRef.current;
      uploadFilesOpeningChildRef.current = null;
      if (next === "activityResultUploadModal") {
        uploadExpectParentAfterChildCloseRef.current =
          "temporalActivityUploadModal";
        reopenUploadFilesModal("activityResultUploadModal");
        return;
      }
      setUploadSheetsBlurOpen(false);
      if (upload) Modal.getInstance(upload)?.dispose();
      removeBootstrapBodyBackdrops();
    };

    const onResultsHidden = () => {
      const reopenParent = uploadExpectParentAfterChildCloseRef.current;
      uploadExpectParentAfterChildCloseRef.current = null;
      if (reopenParent === "temporalActivityUploadModal") {
        reopenUploadFilesModal("temporalActivityUploadModal");
        return;
      }
      setUploadSheetsBlurOpen(false);
      if (results) Modal.getInstance(results)?.dispose();
      removeBootstrapBodyBackdrops();
    };

    upload?.addEventListener("hidden.bs.modal", onUploadHidden);
    results?.addEventListener("hidden.bs.modal", onResultsHidden);
    return () => {
      upload?.removeEventListener("hidden.bs.modal", onUploadHidden);
      results?.removeEventListener("hidden.bs.modal", onResultsHidden);
    };
  }, []);

  useEffect(() => {
    if (!uploadSheetsBlurOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.classList.add("modal-open");
    document.body.style.overflow = "hidden";
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = prevOverflow;
    };
  }, [uploadSheetsBlurOpen]);

  useEffect(() => {
    getAllUser();
    fetchAllTasks();
    fetchAllClientGroups();

    // Set up a listener for the dashboard modal
    const dashboardModal = document.getElementById("dashboardModal");
    if (dashboardModal) {
      dashboardModal.addEventListener("shown.bs.modal", () => {
        // Automatically fetch dashboard data when modal is shown
        fetchDashboardData();
      });
    }
  }, []);

  useEffect(() => {
    const fetchAvailableDates = async () => {
      try {
        const response: any = await getAvailableDates();
        if (response?.data && Array.isArray(response.data)) {
          // const sortedDates = [...response.data]
          setAvailableDates(response.data);
          // Don't auto-set dates - let user select them manually
        }
      } catch (error) {
        console.error("Error fetching available dates:", error);
      }
    };
    fetchAvailableDates();
  }, []);

  useEffect(() => {
    const reportModal = document.getElementById("exampleModalToggle4");
    const onReportHidden = () => {
      setReport("");
      setCurrentReportType(""); // Reset the report type
      setFilters({
        // Reset filters
        tabular_view: "normal",
        startDate: "",
        endDate: "",
        search: "",
      });
      afterNestedReportChildModalHidden();
    };
    reportModal?.addEventListener("hidden.bs.modal", onReportHidden);
    return () => reportModal?.removeEventListener("hidden.bs.modal", onReportHidden);
  }, []);

  useEffect(() => {
    const worklogModal = document.getElementById("devWorklogModal");
    const onHidden = () => {
      setWorklogData(null);
      setWorklogError("");
    };
    worklogModal?.addEventListener("hidden.bs.modal", onHidden);
    return () => worklogModal?.removeEventListener("hidden.bs.modal", onHidden);
  }, []);

  const showWorklogReport = worklogData != null && !worklogLoading;

  const handleWorklogBack = () => {
    setWorklogData(null);
    setWorklogError("");
  };

  // Separate function to fetch dashboard data (without form submission)
  const fetchDashboardData = async () => {
    // Validate date selection
    // if (!selectedStartDate || !selectedEndDate) {
    //   alert("Please select both start and end dates")
    //   return
    // }

    if (selectedStartDate && selectedEndDate) {
      const dashboardRangeErr = getDateRangeOrderError(
        selectedStartDate,
        selectedEndDate,
      );
      if (dashboardRangeErr) {
        toast.error(dashboardRangeErr);
        return;
      }
    }

    setDashboardLoading(true);
    try {
      console.log(
        "Fetching dashboard data from",
        selectedStartDate,
        "to",
        selectedEndDate,
      );
      const response = await getDashboard({
        start_date: selectedStartDate,
        end_date: selectedEndDate,
      });
      console.log("Dashboard response:", response);
      if (response && response.data && response.data.dashboard) {
        setDashboardData(response.data.dashboard);
      } else {
        console.error("Invalid dashboard data format", response);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Handle form submission for dashboard
  const handleGetDashboard = async (e: any) => {
    e.preventDefault();
    fetchDashboardData();
  };

  const fetchDevWorklog = async (e?: FormEvent) => {
    e?.preventDefault();
    const rangeErr = getDateRangeOrderError(worklogStartDate, worklogEndDate);
    if (rangeErr) {
      toast.error(rangeErr);
      return;
    }
    setWorklogLoading(true);
    setWorklogError("");
    setWorklogData(null);
    try {
      const response = await getDevWorklog({
        userId: DEV_WORKLOG_USER_ID,
        startDate: format(worklogStartDate, "dd-MM-yyyy"),
        endDate: format(worklogEndDate, "dd-MM-yyyy"),
      });
      setWorklogData(response?.data ?? null);
    } catch (err: unknown) {
      setWorklogData(null);
      const message =
        err &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { data?: { message?: string; detail?: string } } })
          .response?.data
          ? (err as { response: { data: { message?: string; detail?: string } } })
              .response.data.message ||
            (err as { response: { data: { detail?: string } } }).response.data
              .detail
          : null;
      setWorklogError(message || "Failed to load worklog.");
    } finally {
      setWorklogLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setUserData({
      ...userData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Update reset userData to include all properties
  const resetUserData = () => {
    return {
      name: "",
      user_id: "",
      password: "",
      isAdmin: false,
      endDate: new Date(),
      startDate: new Date(),
      periodType: "",
      financialYear: "",
      financialPeriod: "",
      periodStartDate: new Date(),
      periodEndDate: new Date(),
      taskName: "",
      byTaskStartDate: new Date(),
      byTaskEndDate: new Date(),
      clientGroupName: "",
      byClientGroupStartDate: new Date(),
      byClientGroupEndDate: new Date(),
    };
  };

  // Handle form submission
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // Process form data here, e.g., send it to a server
    console.log("Form submitted with data:", userData);
    try {
      await addUser(userData);
    } catch (error) {}

    // Reset form after submission
    setUserData({
      name: "",
      user_id: "",
      password: "",
      isAdmin: false,
      endDate: new Date(),
      startDate: new Date(),
      periodType: "",
      financialYear: "",
      financialPeriod: "",
      periodStartDate: new Date(),
      periodEndDate: new Date(),
      taskName: "",
      byTaskStartDate: new Date(),
      byTaskEndDate: new Date(),
      clientGroupName: "",
      byClientGroupStartDate: new Date(),
      byClientGroupEndDate: new Date(),
    });
  };

  const handleDelete = async (e: any) => {
    e.preventDefault();
    // Implement your delete logic here
    console.log("Deleting user with ID:", user_id);
    try {
      await deleteUser({ user_id });
    } catch (error) {}
    // Optionally reset the form or close the modal
    setUserId("");
    // If you're using Bootstrap's modal toggle, you can also close the modal programmatically
    // e.g., using Bootstrap modal methods (document.getElementById('exampleModalToggle2').modal('hide'))
  };

  const getAllUser = async () => {
    const response = await getAllUsers();
    console.log(response, "Response");
    setUsers(response.data);
  };

  const fetchAllTasks = async () => {
    const response: any = await getAllTasks();
    console.log(response, "Response");
    setTasks(response.data);
  };

  const fetchAllClientGroups = async () => {
    const response: any = await getAllClientGroups();
    console.log(response, "Response");
    setClientGroups(response.data);
  };

  const generateWeeklyReport = async () => {
    // const response = await generateReport({ userId: selectedUser })
    // if (Array.isArray(response?.data)) {
    //   setSelectedUser("")
    //   // Join the array of strings with a new line and set it to the report
    //   setReport(response.data.join('\n'));
    // } else {
    //   console.error('Response data is not an array');
    // }
  };
  const generateAllWeeklyReports = async () => {
    const response = await getAllUsersReport();
    let reports: any = [];
    for (let i = 0; i < response?.data?.length; i++) {
      reports.push({
        reports: response?.data[i].reports.join("\n"),
        id: response?.data[i]?.UserID,
      });
    }
    setAllReports(reports);
  };

  const changeStartDate = (date: any) => {
    const formattedEndDate = format(date, "dd-MM-yyyy");

    setStartDate(date);
  };

  const changeEndDate = (date: any) => {
    const formattedEndDate = format(date, "dd-MM-yyyy");

    setEndDate(date);
  };

  const formatDate = (date: any) => {
    console.log(date);
    const [year, month, day] = date.split("-"); // Split the input date (YYYY-MM-DD)
    return `${day}-${month}-${year}`; // Return it in DD-MM-YYYY format
  };

  useEffect(() => {
    const modal = document.getElementById("weeklyReport");
    if (modal) {
      modal.addEventListener("hidden.bs.modal", () => {
        setUserData({
          name: "",
          user_id: "",
          password: "",
          isAdmin: false,
          endDate: new Date(),
          startDate: new Date(),
          periodType: "",
          financialYear: "",
          financialPeriod: "",
          periodStartDate: new Date(),
          periodEndDate: new Date(),
          taskName: "",
          byTaskStartDate: new Date(),
          byTaskEndDate: new Date(),
          clientGroupName: "",
          byClientGroupStartDate: new Date(),
          byClientGroupEndDate: new Date(),
        });
        setReport("");
        clearStaleBootstrapModalChrome();
      });
    }
  }, []);

  useEffect(() => {
    const reopenChoiceIfNeeded = () => {
      window.setTimeout(() => {
        const reportEl = document.getElementById("weeklyReport");
        const choiceEl = document.getElementById("exampleModalToggle9");
        const generateReportEl = document.getElementById("exampleModalToggle3");
        if (!choiceEl || reportEl?.classList.contains("show")) return;

        trimExcessModalBackdrops();

        // Restore stack: Generate Report → Weekly choice (avoids orphan backdrop / blank stack).
        if (generateReportEl && !generateReportEl.classList.contains("show")) {
          Modal.getOrCreateInstance(generateReportEl).show();
        }
        Modal.getOrCreateInstance(choiceEl).show();
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => trimExcessModalBackdrops());
        });
      }, 100);
    };

    const resetPeriodFields = () => {
      setUserData((prevData: any) => ({
        ...prevData,
        periodType: "",
        financialYear: "",
        financialPeriod: "",
        periodStartDate: new Date(),
        periodEndDate: new Date(),
      }));
    };

    const dateEl = document.getElementById("weeklyReportDateModal");
    const periodEl = document.getElementById("weeklyReportPeriodModal");
    const choiceEl = document.getElementById("exampleModalToggle9");

    const onDateHidden = () => reopenChoiceIfNeeded();
    const onPeriodHidden = () => {
      resetPeriodFields();
      reopenChoiceIfNeeded();
    };
    const onChoiceHidden = () => {
      window.setTimeout(() => {
        const dateOpen =
          document.getElementById("weeklyReportDateModal")?.classList.contains("show");
        const periodOpen =
          document.getElementById("weeklyReportPeriodModal")?.classList.contains("show");
        if (dateOpen || periodOpen) return;
        setReport("");
        clearStaleBootstrapModalChrome();
      }, 150);
    };

    dateEl?.addEventListener("hidden.bs.modal", onDateHidden);
    periodEl?.addEventListener("hidden.bs.modal", onPeriodHidden);
    choiceEl?.addEventListener("hidden.bs.modal", onChoiceHidden);

    return () => {
      dateEl?.removeEventListener("hidden.bs.modal", onDateHidden);
      periodEl?.removeEventListener("hidden.bs.modal", onPeriodHidden);
      choiceEl?.removeEventListener("hidden.bs.modal", onChoiceHidden);
    };
  }, []);

  useEffect(() => {
    const taskEl = document.getElementById("reportByTaskModal");
    const clientGroupEl = document.getElementById("reportByClientGroupModal");
    const onJobOrClientModalHidden = () => afterNestedReportChildModalHidden();
    taskEl?.addEventListener("hidden.bs.modal", onJobOrClientModalHidden);
    clientGroupEl?.addEventListener("hidden.bs.modal", onJobOrClientModalHidden);
    return () => {
      taskEl?.removeEventListener("hidden.bs.modal", onJobOrClientModalHidden);
      clientGroupEl?.removeEventListener("hidden.bs.modal", onJobOrClientModalHidden);
    };
  }, []);

  const sendMessage = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }

    const aiWeekRangeErr = getDateRangeOrderError(startDate, endDate);
    if (aiWeekRangeErr) {
      setError(aiWeekRangeErr);
      return;
    }

    if (newMessage.trim() === "") {
      setError("Message cannot be empty.");
      return;
    }

    setError(""); // Clear any previous errors

    // Immediately add user's message to history
    const userQueryMessage = {
      query: newMessage,
      type: "query",
    };

    // Add loading state
    const loadingMessage = {
      type: "loading",
    };

    setHistory((prevHistory: any[]) => [
      ...prevHistory,
      userQueryMessage,
      loadingMessage,
    ]);

    const query = {
      userId: selectedUser,
      query: newMessage,
      weekEnd: format(endDate, "dd-MM-yyyy"),
      weekStart: format(startDate, "dd-MM-yyyy"),
    };

    setNewMessage(""); // Clear the input field

    try {
      const response = await reportAI(query);

      // Update history by removing loading state and adding response
      setHistory((prevHistory: any[]) => {
        // Remove the loading message
        const filteredHistory = prevHistory.filter(
          (item) => item.type !== "loading",
        );

        // Add the response
        return [
          ...filteredHistory,
          {
            query: userQueryMessage.query,
            response: response?.data.message,
            type: "response",
          },
        ];
      });
    } catch (err) {
      console.error("Error sending message:", err);

      // Update history by removing loading state and adding error
      setHistory((prevHistory: any[]) => {
        // Remove the loading message
        const filteredHistory = prevHistory.filter(
          (item) => item.type !== "loading",
        );

        // Add the error
        return [
          ...filteredHistory,
          {
            query: userQueryMessage.query,
            type: "error",
            error: "An error occurred. Please try again.",
          },
        ];
      });
    }
  };

  const submitAiGithubProjectReport = async (e: any) => {
    e.preventDefault();
    if (!aiGithubProjectName.trim()) {
      setAiGithubProjectError("Project name is required.");
      return;
    }
    const aiGithubRangeErr = getDateRangeOrderError(
      aiGithubProjectStart,
      aiGithubProjectEnd,
    );
    if (aiGithubRangeErr) {
      setAiGithubProjectError(aiGithubRangeErr);
      return;
    }
    setAiGithubProjectError("");
    setAiGithubProjectLoading(true);
    try {
      const res = await reportGithubChangesByProject({
        projectName: aiGithubProjectName.trim(),
        startDate: format(aiGithubProjectStart, "yyyy-MM-dd"),
        endDate: format(aiGithubProjectEnd, "yyyy-MM-dd"),
      });
      setAiGithubProjectResult(res?.data ?? null);
    } catch {
      setAiGithubProjectError("Something went wrong. Please try again.");
    } finally {
      setAiGithubProjectLoading(false);
    }
  };

  const handleReportByTaskSubmit = async (e: any) => {
    e.preventDefault();
    const byTaskRangeErr = getDateRangeOrderError(
      userData?.byTaskStartDate,
      userData?.byTaskEndDate,
    );
    if (byTaskRangeErr) {
      toast.error(byTaskRangeErr);
      return;
    }
    const formattedStartDate = userData?.byTaskStartDate
      ? format(userData.byTaskStartDate, "dd-MM-yyyy")
      : "";
    const formattedEndDate = userData?.byTaskEndDate
      ? format(userData.byTaskEndDate, "dd-MM-yyyy")
      : "";
    const filters: { startDate?: string; endDate?: string } = {};
    if (formattedStartDate) {
      filters.startDate = formattedStartDate;
    }
    if (formattedEndDate) {
      filters.endDate = formattedEndDate;
    }
    const response = await generateReportByTask(userData.taskName, filters);

    if (Array.isArray(response?.data)) {
      setReport(response.data.join("\n"));
      setSelectedUser("");
      setUserData(resetUserData());
      window.setTimeout(showReportOutputModal, 150);
    } else {
      console.error("Response data is not an array");
    }
  };

  const handleReportByClientGroupSubmit = async (e: any) => {
    e.preventDefault();
    const byClientGroupRangeErr = getDateRangeOrderError(
      userData?.byClientGroupStartDate,
      userData?.byClientGroupEndDate,
    );
    if (byClientGroupRangeErr) {
      toast.error(byClientGroupRangeErr);
      return;
    }
    const formattedStartDate = format(
      userData?.byClientGroupStartDate,
      "dd-MM-yyyy",
    );
    const formattedEndDate = format(
      userData?.byClientGroupEndDate,
      "dd-MM-yyyy",
    );
    const filters: { startDate?: string; endDate?: string } = {};
    if (formattedStartDate) {
      filters.startDate = formattedStartDate;
    }
    if (formattedEndDate) {
      filters.endDate = formattedEndDate;
    }
    const response = await generateReportByClientGroup(
      userData.clientGroupName,
      filters,
    );

    if (Array.isArray(response?.data)) {
      setReport(response.data.join("\n"));
      setSelectedUser("");
      setUserData(resetUserData());
      window.setTimeout(showReportOutputModal, 150);
    } else {
      console.error("Response data is not an array");
    }
  };

  const handleWeeklyReportSubmit = async (e: any) => {
    // const response = await generateReport({ userId: selectedUser })

    // if (Array.isArray(response?.data)) {

    //   setSelectedUser("")
    //   // Join the array of strings with a new line and set it to the report
    //   setReport(response.data.join('\n'));
    // } else {
    //   console.error('Response data is not an array');
    // }

    e.preventDefault();
    const adminWeeklyMainErr = getDateRangeOrderError(
      userData?.startDate,
      userData?.endDate,
    );
    if (adminWeeklyMainErr) {
      toast.error(adminWeeklyMainErr);
      return;
    }
    if (userData.periodType === "durationPeriod") {
      const adminWeeklyPeriodErr = getDateRangeOrderError(
        userData?.periodStartDate,
        userData?.periodEndDate,
      );
      if (adminWeeklyPeriodErr) {
        toast.error(adminWeeklyPeriodErr);
        return;
      }
    }
    const formattedStartDate = format(userData?.startDate, "dd-MM-yyyy");
    const formattedEndDate = format(userData?.endDate, "dd-MM-yyyy");

    let reportParams: any = {
      userId: selectedUser,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      periodType: userData?.periodType,
    };

    if (userData.periodType === "durationPeriod") {
      reportParams = {
        ...reportParams,
        periodStartDate: format(userData?.periodStartDate, "dd-MM-yyyy"),
        periodEndDate: format(userData?.periodEndDate, "dd-MM-yyyy"),
        financialPeriod: null,
        financialYear: null,
      };
    } else if (userData?.periodType === "financialTimeFrame") {
      reportParams = {
        ...reportParams,
        periodStartDate: null,
        periodEndDate: null,
        financialPeriod: userData?.financialPeriod,
        financialYear: userData?.financialYear,
      };
    }

    try {
      const response = await generateReport(reportParams);

      if (Array.isArray(response?.data)) {
        setReport(response.data.join("\n"));
        setSelectedUser("");
        setUserData({
          name: "",
          user_id: "",
          password: "",
          isAdmin: false,
          endDate: new Date(),
          startDate: new Date(),
          periodType: "",
          financialYear: "",
          financialPeriod: "",
          periodStartDate: new Date(),
          periodEndDate: new Date(),
          taskName: "",
          byTaskStartDate: new Date(),
          byTaskEndDate: new Date(),
          clientGroupName: "",
          byClientGroupStartDate: new Date(),
          byClientGroupEndDate: new Date(),
        });
      } else {
        console.error("Response data is not an array");
      }
    } catch (e) {
      console.log(e);
    }
  };
  const handleGetTracking = async (e: any) => {
    setTrackingDataLoading(true);
    try {
      e.preventDefault();
      const response = await getTracking(selectedActivity);
      setTrackingData(response.data.Response);
      setTrackingDataLoading(false);
    } catch (error) {
      setTrackingDataLoading(false);
    }
  };

  const handleSheetsUpload = async (e: any) => {
    e.preventDefault();
    setUploadDataLoading(true);
    try {
      const formData = new FormData();
      formData.append("sheet1", sheet1);
      formData.append("sheet2", sheet2);
      const response = await uploadSheets(formData);
      console.log(response);
      setUploadResults(response?.data?.message);
      setUploadDataLoading(false);
    } catch (error) {
      console.log("error in uploading sheets, ", error);
      setUploadDataLoading(false);
    }
  };

  const openUploadSheetsModalFromCard = () => {
    uploadExpectParentAfterChildCloseRef.current = null;
    uploadFilesOpeningChildRef.current = null;
    removeBootstrapBodyBackdrops();
    setUploadSheetsBlurOpen(true);
    window.requestAnimationFrame(() => {
      getUploadSheetsModalInstance("temporalActivityUploadModal")?.show();
    });
  };

  const closeUploadSheetsModal = () => {
    uploadExpectParentAfterChildCloseRef.current = null;
    uploadFilesOpeningChildRef.current = null;
    getUploadSheetsModalInstance("temporalActivityUploadModal")?.hide();
  };

  const submitUploadSheetsAndShowResults = (e: MouseEvent) => {
    e.preventDefault();
    uploadFilesOpeningChildRef.current = "activityResultUploadModal";
    void handleSheetsUpload(e);
    getUploadSheetsModalInstance("temporalActivityUploadModal")?.hide();
  };

  const backFromUploadResultsModal = () => {
    uploadExpectParentAfterChildCloseRef.current =
      "temporalActivityUploadModal";
    getUploadSheetsModalInstance("activityResultUploadModal")?.hide();
  };

  const closeUploadResultsModal = () => {
    uploadExpectParentAfterChildCloseRef.current = null;
    uploadFilesOpeningChildRef.current = null;
    getUploadSheetsModalInstance("activityResultUploadModal")?.hide();
  };

  const handleUploadSheetsBackdropClick = () => {
    const resultsEl = document.getElementById("activityResultUploadModal");
    if (resultsEl?.classList.contains("show")) {
      closeUploadResultsModal();
      return;
    }
    closeUploadSheetsModal();
  };

  const handleDateChange = (date: any) => {
    setUserData((prevData: any) => ({
      ...prevData,
      endDate: date, // Set the date directly
    }));
  };

  const handleStartDateChange = (date: any) => {
    setUserData((prevData: any) => ({
      ...prevData,
      startDate: date,
    }));
  };
  const handlePeriodStartDateChange = (date: any) => {
    setUserData((prevData: any) => ({
      ...prevData,
      periodStartDate: date,
    }));
  };
  const handlePeriodEndDateChange = (date: any) => {
    setUserData((prevData: any) => ({
      ...prevData,
      periodEndDate: date, // Set the date directly
    }));
  };

  const hideBootstrapModalById = (id: string) => {
    const el = document.getElementById(id);
    if (el) Modal.getOrCreateInstance(el).hide();
  };

  /** Report output lives in `#exampleModalToggle4`; open only after validation + fetch succeed. */
  const showReportOutputModal = () => {
    const el = document.getElementById("exampleModalToggle4");
    if (el) Modal.getOrCreateInstance(el).show();
  };

  // Update the handleTaskReportSubmit function
  const handleTaskReportSubmit = async (e: any) => {
    e.preventDefault();
    const taskReportRangeErr = getDateRangeOrderError(
      userData?.byTaskStartDate,
      userData?.byTaskEndDate,
    );
    if (taskReportRangeErr) {
      toast.error(taskReportRangeErr);
      return;
    }
    const formattedStartDate = userData?.byTaskStartDate
      ? format(userData.byTaskStartDate, "dd-MM-yyyy")
      : "";
    const formattedEndDate = userData?.byTaskEndDate
      ? format(userData.byTaskEndDate, "dd-MM-yyyy")
      : "";
    const filters: { startDate?: string; endDate?: string } = {};
    if (formattedStartDate) {
      filters.startDate = formattedStartDate;
    }
    if (formattedEndDate) {
      filters.endDate = formattedEndDate;
    }
    const response = await generateReportByTask(userData.taskName, filters);

    // if (Array.isArray(response?.data)) {
    setReport(response.data);
    setSelectedUser("");
    setUserData({
      name: "",
      user_id: "",
      password: "",
      isAdmin: false,
      endDate: new Date(),
      startDate: new Date(),
      periodType: "",
      financialYear: "",
      financialPeriod: "",
      periodStartDate: new Date(),
      periodEndDate: new Date(),
      taskName: "",
      byTaskStartDate: new Date(),
      byTaskEndDate: new Date(),
      clientGroupName: "",
      byClientGroupStartDate: new Date(),
      byClientGroupEndDate: new Date(),
    });
    hideBootstrapModalById("reportByTaskModal");
    window.setTimeout(showReportOutputModal, 150);
    // } else {
    //   console.error('Response data is not an array');
    // }
  };

  const generateAllUsersWeeklyReport = async () => {
    const response: any = await getAllUsersWeeklyReport();
    console.log(response, " Response");
    // const sampleReport="<h2>Weekly Task Logs Report</h2><p><strong>Period:</strong> 28-08-2025 to 04-09-2025</p><hr><section style=\"margin-bottom:24px;\"><h3>User: admin1</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: Andrew</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: Annie</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: bilaltest</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: Carlo</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: daniyal</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br>--------------------------------------------------------------------<br>Task ID: 148, <br>Job ID: Tax Genni, <br>Client Group: Andrew, <br>Subtask Details:<br><br>---------------- Subtask Report ----------------<br>Subtask Name: DataSources Delimiting,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Jacinto Latest Update,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Scrapping Complete (Legal),<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Scraped data preprocessing,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing , Production update,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Tree.json changes,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Uploading data to azure ai search,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing of new data scrapped,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: keyword search frotnend,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: keyword search backend,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing of keyword search,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deployment to production and testing,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Keywords search on reverse search,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: General Ato website scrapping,<br>Estimated Time: 6 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Tree.json update,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: upload data to azure ai search,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Refrence bug resolved, conflits between new data and tree.json ,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing bug fixes and deployment production,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Visulization chat history ,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Reverse chat history ,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Agent search- Simple tax calculator,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Agent search- tax withheld calculator ,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: simple tax calculator backend,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: simple tax calculator frontend,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: tax withheld calculator backend ,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: tax withheld frontend,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: medicare  levy backend,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: medicare levy frontend ,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: super gurantee calculator backend,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: super gurantee calculator frontend ,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: income tax estimator calculator backend,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: frontend complete income tax estimator calculator,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: home office expense calculator,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: home office expense calculator frontend complete flow,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: chatbot against all calculators backend,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: chatbot in calculators frontend,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: latest data bug,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deployment and testing,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: do i need to lodge tax return frontend,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: complete backend of determination of residence calcualtor ,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: complete frontend of determination of residence calcualtor,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: division 7a calculator complete calculator backend,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: division 7a calculator complete calculator frontend,<br>Estimated Time: 6 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deployment to production,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: User managment, login and register apis, frontend and backend,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: public to private variables,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: calculator fixes,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: nginx conf and ssl certificates bug fix,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: frontend fixes,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>Total Time Logged for Task: 0 hrs 0 mins<br><br>--------------------------------------------------------------------<br><br>\n\n<br><br>--------------------------------------------------------------------<br>Task ID: 149, <br>Job ID: BSearch, <br>Client Group: Andrew, <br>Subtask Details:<br><br>---------------- Subtask Report ----------------<br>Subtask Name: RPC update,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: PDF URl,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: merc screen bug,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing of merc screen and deployment to server,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: ledger screen dropdown,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>Total Time Logged for Task: 0 hrs 0 mins<br><br>--------------------------------------------------------------------<br><br>\n\n<br><br>--------------------------------------------------------------------<br>Task ID: 150, <br>Job ID: Tracker Project, <br>Client Group: Andrew, <br>Subtask Details:<br><br>---------------- Subtask Report ----------------<br>Subtask Name: AI descriptions,<br>Estimated Time: 2 hrs 30 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Github Integrations,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: SubTasks,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Subtask,<br>Estimated Time: 6 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Changes from Lizza ,<br>Estimated Time: 2 hrs 30 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing and Production Update,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Reports Generation changes,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: changes requested by lizza,annie,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: testing and bug fixes,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: production update, testing on production,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Frontend Dashboard,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Design Sorting Algorithm for Activity Score,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Implement Scoring Algorithm in Code,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Implement Backend Aggregation Logic,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Add Timeline and Trend Indicators,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Prepare Aggregated Metrics and Metadata,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: testing and bug fixes, deployment to production,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: change to temporal tracker ,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Design bugs fixes and structural changes,<br>Estimated Time: 4 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Temporal activity tracking using new file structures and improved algorithms, capturing stage and status changes,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Dashboard data imrpoved and structural change, complete rewritten algorithm,<br>Estimated Time: 6 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: csvs management ,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deployment to production, frontend new design and backend complete,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Refine dashboard output,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: frontend work detail dashboard upon clicking,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Databases checks for none values to prevent breaking of code at multiple places,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deployment to production and testing,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>Total Time Logged for Task: 0 hrs 0 mins<br><br>--------------------------------------------------------------------<br><br>\n\n<br><br>--------------------------------------------------------------------<br>Task ID: 151, <br>Job ID: Accounting Transforms, <br>Client Group: Andrew, <br>Subtask Details:<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Change In Keys Mapping,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Testing and production Update,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Robust,<br>Estimated Time: 6 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Incomplete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Design change, for journal transforms,,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Backend apis for journal transforms,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: Frontend integration journal transforms,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deployment,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: journal transform change if should be column is not given,<br>Estimated Time: 5 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deployment to production,<br>Estimated Time: 1 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: deprecation transform fixes,<br>Estimated Time: 3 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>---------------- Subtask Report ----------------<br>Subtask Name: testing and deployment,<br>Estimated Time: 2 hrs 0 mins,<br>Logged Time: 0 hrs 0 mins,<br>Status: Complete<br><br>Total Time Logged for Task: 0 hrs 0 mins<br><br>--------------------------------------------------------------------<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: Lizza _Admin</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: Lizza_1</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: test8787</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: testing</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section><section style=\"margin-bottom:24px;\"><h3>User: waleedtest</h3><h5>Report 1:</h5> <br> Total Time Logged by User<br>Total Time Logged: 0 hrs 0 mins<br><h5>Report 2:</h5> <br> Task-Specific Report<br><br><h5>Report 3:</h5> <br> Client Group-Specific Report<br><br></section>"
    if (Array.isArray(response?.data)) {
      // setSelectedUser("")
      //   // Join the array of strings with a new line and set it to the report
      setReport(response.data.join("\n"));
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      tabular_view: "normal",
      startDate: "",
      endDate: "",
      search: "",
    };

    setFilters(clearedFilters);

    // Refresh the report with cleared filters
    if (
      currentReportType === "openJobsByClientGroup" ||
      currentReportType === "openJobsByJobName" ||
      currentReportType === "teamMemberSummary" ||
      currentReportType === "closedJobsByClientGroup" ||
      currentReportType === "closedJobsByJobName" ||
      currentReportType === "clientGroupUserHours"
    ) {
      applyFilters(clearedFilters);
    }
  };

  // Update the handleClientGroupReportSubmit function
  const handleClientGroupReportSubmit = async (e: any) => {
    e.preventDefault();
    const clientGroupReportRangeErr = getDateRangeOrderError(
      userData?.byClientGroupStartDate,
      userData?.byClientGroupEndDate,
    );
    if (clientGroupReportRangeErr) {
      toast.error(clientGroupReportRangeErr);
      return;
    }
    const formattedStartDate = userData?.byClientGroupStartDate
      ? format(userData.byClientGroupStartDate, "dd-MM-yyyy")
      : "";
    const formattedEndDate = userData?.byClientGroupEndDate
      ? format(userData.byClientGroupEndDate, "dd-MM-yyyy")
      : "";
    const filters: { startDate?: string; endDate?: string } = {};
    if (formattedStartDate) {
      filters.startDate = formattedStartDate;
    }
    if (formattedEndDate) {
      filters.endDate = formattedEndDate;
    }
    const response = await generateReportByClientGroup(
      userData.clientGroupName,
      filters,
    );

    // if (Array.isArray(response?.data)) {
    setReport(response.data);
    setSelectedUser("");
    setUserData({
      name: "",
      user_id: "",
      password: "",
      isAdmin: false,
      endDate: new Date(),
      startDate: new Date(),
      periodType: "",
      financialYear: "",
      financialPeriod: "",
      periodStartDate: new Date(),
      periodEndDate: new Date(),
      taskName: "",
      byTaskStartDate: new Date(),
      byTaskEndDate: new Date(),
      clientGroupName: "",
      byClientGroupStartDate: new Date(),
      byClientGroupEndDate: new Date(),
    });
    hideBootstrapModalById("reportByClientGroupModal");
    window.setTimeout(showReportOutputModal, 150);
    // }
    // else {
    //   console.error('Response data is not an array');
    // }
  };

  const generateTeamMemberSummaryReport = async (effectiveFilters?: any) => {
    try {
      setCurrentReportType("teamMemberSummary"); // Use consistent name
      setReport("");

      const params: any = {};

      if (effectiveFilters?.tabular_view === "tabular") {
        params.tabular_view = "1";
      }

      // Date filters
      if (effectiveFilters?.startDate) {
        params.startDate = formatDateToAustralian(effectiveFilters.startDate);
      }

      if (effectiveFilters?.endDate) {
        params.endDate = formatDateToAustralian(effectiveFilters.endDate);
      }

      const response: any = await generateReportByTeamMemberSummary(params);

      if (Array.isArray(response?.data)) {
        setReport(response.data.join("\n"));
      } else if (response?.data) {
        setReport(
          typeof response.data === "string"
            ? response.data
            : JSON.stringify(response.data),
        );
      } else {
        setReport("No data available for the selected filters.");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setReport("Error fetching report. Please try again.");
    }
  };

  // Handler functions
  const handleDisplayTypeChange = (value: string) => {
    setFilters((prev) => {
      const updated = { ...prev, tabular_view: value };
      if (currentReportType === "teamMemberSummary") {
        setTimeout(() => applyFilters(updated), 0);
      }
      return updated;
    });
  };

  const isGroupedClientJobsResponse = <T,>(
    value: unknown,
  ): value is GroupedClientJobsResponse<T> => {
    return !!value && typeof value === "object" && !Array.isArray(value);
  };

  const setAllExpanded = (
    groups: Record<string, unknown>,
  ): Record<string, boolean> => {
    return Object.fromEntries(Object.keys(groups).map((key) => [key, true]));
  };

  const formatHours = (value: unknown): string => {
    if (typeof value !== "number" || Number.isNaN(value)) return "0";
    return Number.isInteger(value) ? `${value}` : value.toFixed(2);
  };

  const formatFieldLabel = (key: string): string => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (char) => char.toUpperCase())
      .trim();
  };

  const formatFieldValue = (
    key: string,
    value: unknown,
    row?: Record<string, unknown>,
  ): string => {
    if (
      key === "githubLink" ||
      key === "github_link" ||
      key === "githubLinks" ||
      key === "github_links"
    ) {
      const links = parseGithubLinksFromRaw(value);
      return links.length ? links.join("\n") : "-";
    }
    if (key === "status") {
      const label = completionStatusLabel(value, row?.statusBoolean);
      return label === "—" ? "-" : label;
    }
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "boolean") return value ? "True" : "False";
    return String(value);
  };

  const handleOpenJobsByClientGroupViewModeChange = (value: ReportViewMode) => {
    setOpenJobsByClientGroupViewMode(value);
    if (currentReportType === "openJobsByClientGroup") {
      getReportByOpenJobsByClientGroup(filters, undefined, value);
    }
  };

  const handleClosedJobsByClientGroupViewModeChange = (
    value: ReportViewMode,
  ) => {
    setClosedJobsByClientGroupViewMode(value);
    if (currentReportType === "closedJobsByClientGroup") {
      getReportByClosedJobsByClientGroup(filters, undefined, value);
    }
  };

  const handleStartDateChangeClientJob = (value: string) => {
    setFilters((prev) => ({ ...prev, startDate: value }));
  };

  const handleEndDateChange = (value: string) => {
    setFilters((prev) => ({ ...prev, endDate: value }));
  };

  const applyFilters = (effectiveFilters = filters) => {
    console.log("Current report type:", currentReportType);

    const f = effectiveFilters ?? filters;
    if (f.startDate && f.endDate) {
      const tableReportRangeErr = getDateRangeOrderError(
        f.startDate,
        f.endDate,
      );
      if (tableReportRangeErr) {
        toast.error(tableReportRangeErr);
        return;
      }
    }

    if (currentReportType === "openJobsByClientGroup") {
      getReportByOpenJobsByClientGroup(effectiveFilters, {
        sortColumn: openJobsSortColumn,
        sortDirection: openJobsSortDirection,
      });
    } else if (currentReportType === "openJobsByJobName") {
      getReportByOpenJobsByJobName(effectiveFilters, {
        sortColumn: openJobsByJobNameSortColumn,
        sortDirection: openJobsByJobNameSortDirection,
      });
    } else if (currentReportType === "teamMemberSummary") {
      generateTeamMemberSummaryReport(effectiveFilters);
    } else if (currentReportType === "closedJobsByClientGroup") {
      getReportByClosedJobsByClientGroup(effectiveFilters, {
        sortColumn: closedJobsByClientGroupSortColumn,
        sortDirection: closedJobsByClientGroupSortDirection,
      });
    } else if (currentReportType === "closedJobsByJobName") {
      getReportByClosedJobsByJobName(effectiveFilters, {
        sortColumn: closedJobsByJobNameSortColumn,
        sortDirection: closedJobsByJobNameSortDirection,
      });
    } else if (currentReportType === "clientGroupUserHours") {
      getClientGroupUserHoursReport(effectiveFilters);
    } else {
      console.log("Filters are only available for certain report types");
    }
  };

  const getReportByOpenJobsByClientGroup = async (
    effectiveFilters?: typeof filters,
    sortParams?: {
      sortColumn: string | null;
      sortDirection: SortDirection;
    },
    viewModeOverride?: ReportViewMode,
  ) => {
    const f = effectiveFilters ?? filters;
    const activeViewMode = viewModeOverride ?? openJobsByClientGroupViewMode;
    try {
      setCurrentReportType("openJobsByClientGroup");
      setReport("");
      setOpenJobsTableData(null);
      setOpenJobsGroupedCardData(null);
      setOpenJobsExpandedGroups({});
      setOpenJobsLoading(true);

      const params: Record<string, string> = {};

      if (f.startDate) {
        params.startDate = formatDateToAustralian(f.startDate);
      }
      if (f.endDate) {
        params.endDate = formatDateToAustralian(f.endDate);
      }
      if (f.search?.trim()) {
        params.search = f.search.trim();
      }
      params.tabular = String(activeViewMode === "table");

      const sortColumn = sortParams?.sortColumn ?? openJobsSortColumn;
      const sortDirection = sortParams?.sortDirection ?? openJobsSortDirection;
      if (sortColumn) {
        params.sort_column = sortColumn;
        params.sort_direction = sortDirection;
      }
      if (sortParams) {
        setOpenJobsSortColumn(sortParams.sortColumn);
        setOpenJobsSortDirection(sortParams.sortDirection);
      }

      const response: any = await generateReportByOpenJobsByClientGroup(params);
      const payload = response?.data?.data ?? response?.data;
      if (activeViewMode === "table") {
        setOpenJobsGroupedCardData(null);
        setOpenJobsExpandedGroups({});
        if (Array.isArray(payload)) {
          setOpenJobsTableData(payload);
        } else {
          setOpenJobsTableData([]);
        }
      } else {
        setOpenJobsTableData(null);
        if (isGroupedClientJobsResponse<OpenJobsByClientGroupRow>(payload)) {
          setOpenJobsGroupedCardData(payload);
          setOpenJobsExpandedGroups(setAllExpanded(payload));
        } else {
          setOpenJobsGroupedCardData({});
          setOpenJobsExpandedGroups({});
        }
      }
      void loadOpenJobsColumnConfigFromApi();
    } catch (error) {
      console.error("Error fetching report:", error);
      setReport("Error fetching report. Please try again.");
    } finally {
      setOpenJobsLoading(false);
    }
  };

  const handleOpenJobsSort = (columnKey: string) => {
    const newDirection: SortDirection =
      openJobsSortColumn === columnKey && openJobsSortDirection === "asc"
        ? "desc"
        : "asc";
    getReportByOpenJobsByClientGroup(filters, {
      sortColumn: columnKey,
      sortDirection: newDirection,
    });
  };

  const formatDateToAustralian = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const getReportByOpenJobsByJobName = async (
    effectiveFilters?: typeof filters,
    sortParams?: {
      sortColumn: string | null;
      sortDirection: SortDirection;
    },
  ) => {
    const f = effectiveFilters ?? filters;
    try {
      setCurrentReportType("openJobsByJobName");
      setReport("");
      setOpenJobsByJobNameTableData(null);
      setOpenJobsByJobNameLoading(true);

      const params: Record<string, string> = {};
      if (f.startDate) params.startDate = formatDateToAustralian(f.startDate);
      if (f.endDate) params.endDate = formatDateToAustralian(f.endDate);
      if (f.search?.trim()) params.search = f.search.trim();

      const sortColumn = sortParams?.sortColumn ?? openJobsByJobNameSortColumn;
      const sortDirection =
        sortParams?.sortDirection ?? openJobsByJobNameSortDirection;
      if (sortColumn) {
        params.sort_column = sortColumn;
        params.sort_direction = sortDirection;
      }
      if (sortParams) {
        setOpenJobsByJobNameSortColumn(sortParams.sortColumn);
        setOpenJobsByJobNameSortDirection(sortParams.sortDirection);
      }

      const response: any = await generateReportByOpenJobsByJobName(params);
      const rows = response?.data?.data ?? response?.data;
      if (Array.isArray(rows)) {
        setOpenJobsByJobNameTableData(rows);
      } else {
        setOpenJobsByJobNameTableData([]);
      }
      void loadOpenJobsByJobNameColumnConfigFromApi();
    } catch (error) {
      console.error("Error fetching open jobs by job name report:", error);
      setReport("Error fetching report. Please try again.");
    } finally {
      setOpenJobsByJobNameLoading(false);
    }
  };

  const handleOpenJobsByJobNameSort = (columnKey: string) => {
    const newDirection: SortDirection =
      openJobsByJobNameSortColumn === columnKey &&
      openJobsByJobNameSortDirection === "asc"
        ? "desc"
        : "asc";
    getReportByOpenJobsByJobName(filters, {
      sortColumn: columnKey,
      sortDirection: newDirection,
    });
  };

  const getReportByClientGroupActivityRanking = async () => {
    const response: any = await generateReportByClientGroupActivityRanking();
    if (Array.isArray(response?.data)) {
      setReport(response.data.join("\n"));
    }
  };

  const getClientGroupUserHoursReport = async (
    effectiveFilters: typeof filters = filters,
    closedOverride?: boolean,
  ) => {
    try {
      setCurrentReportType("clientGroupUserHours");
      setReport("");
      setClientGroupHoursData(null);
      setClientGroupHoursLoading(true);

      const params: Record<string, string | boolean> = {
        closed: closedOverride ?? clientGroupHoursClosed,
      };
      if (effectiveFilters.startDate) {
        params.startDate = formatDateToAustralian(effectiveFilters.startDate);
      }
      if (effectiveFilters.endDate) {
        params.endDate = formatDateToAustralian(effectiveFilters.endDate);
      }
      if (effectiveFilters.search.trim()) {
        params.search = effectiveFilters.search.trim();
      }

      const response: any =
        await generateClientGroupUserHoursReport(params);
      const payload = response?.data?.data ? response.data : null;
      setClientGroupHoursData(
        payload ?? {
          data: [],
          users: [],
          totalJobs: 0,
          closed: closedOverride ?? clientGroupHoursClosed,
        },
      );
    } catch (error) {
      console.error("Error fetching client group user hours report:", error);
      setReport("Error fetching report. Please try again.");
    } finally {
      setClientGroupHoursLoading(false);
    }
  };

  const getReportByClosedJobsByClientGroup = async (
    effectiveFilters?: typeof filters,
    sortParams?: { sortColumn: string | null; sortDirection: SortDirection },
    viewModeOverride?: ReportViewMode,
  ) => {
    const f = effectiveFilters ?? filters;
    const activeViewMode = viewModeOverride ?? closedJobsByClientGroupViewMode;
    try {
      setCurrentReportType("closedJobsByClientGroup");
      setReport("");
      setClosedJobsByClientGroupTableData(null);
      setClosedJobsByClientGroupGroupedCardData(null);
      setClosedJobsExpandedGroups({});
      setClosedJobsByClientGroupLoading(true);

      const params: Record<string, string> = { ClientGroup: "true" };
      if (f.startDate) params.startDate = formatDateToAustralian(f.startDate);
      if (f.endDate) params.endDate = formatDateToAustralian(f.endDate);
      if (f.search?.trim()) params.search = f.search.trim();
      params.tabular = String(activeViewMode === "table");

      const sortColumn =
        sortParams?.sortColumn ?? closedJobsByClientGroupSortColumn;
      const sortDirection =
        sortParams?.sortDirection ?? closedJobsByClientGroupSortDirection;
      if (sortColumn) {
        params.sort_column = sortColumn;
        params.sort_direction = sortDirection;
      }
      if (sortParams) {
        setClosedJobsByClientGroupSortColumn(sortParams.sortColumn);
        setClosedJobsByClientGroupSortDirection(sortParams.sortDirection);
      }

      const response: any = await generateReportByClosedJobs(params);
      const payload = response?.data?.data ?? response?.data;
      if (activeViewMode === "table") {
        setClosedJobsByClientGroupGroupedCardData(null);
        setClosedJobsExpandedGroups({});
        if (Array.isArray(payload)) {
          setClosedJobsByClientGroupTableData(payload);
        } else {
          setClosedJobsByClientGroupTableData([]);
        }
      } else {
        setClosedJobsByClientGroupTableData(null);
        if (isGroupedClientJobsResponse<ClosedJobsRow>(payload)) {
          setClosedJobsByClientGroupGroupedCardData(payload);
          setClosedJobsExpandedGroups(setAllExpanded(payload));
        } else {
          setClosedJobsByClientGroupGroupedCardData({});
          setClosedJobsExpandedGroups({});
        }
      }
      void loadClosedJobsByClientGroupColumnConfigFromApi();
    } catch (error) {
      console.error("Error fetching closed jobs by client group:", error);
      setReport("Error fetching report. Please try again.");
    } finally {
      setClosedJobsByClientGroupLoading(false);
    }
  };

  const handleClosedJobsByClientGroupSort = (columnKey: string) => {
    const newDirection: SortDirection =
      closedJobsByClientGroupSortColumn === columnKey &&
      closedJobsByClientGroupSortDirection === "asc"
        ? "desc"
        : "asc";
    getReportByClosedJobsByClientGroup(filters, {
      sortColumn: columnKey,
      sortDirection: newDirection,
    });
  };

  const getReportByClosedJobsByJobName = async (
    effectiveFilters?: typeof filters,
    sortParams?: { sortColumn: string | null; sortDirection: SortDirection },
  ) => {
    const f = effectiveFilters ?? filters;
    try {
      setCurrentReportType("closedJobsByJobName");
      setReport("");
      setClosedJobsByJobNameTableData(null);
      setClosedJobsByJobNameLoading(true);

      const params: Record<string, string> = { ClientGroup: "false" };
      if (f.startDate) params.startDate = formatDateToAustralian(f.startDate);
      if (f.endDate) params.endDate = formatDateToAustralian(f.endDate);
      if (f.search?.trim()) params.search = f.search.trim();

      const sortColumn =
        sortParams?.sortColumn ?? closedJobsByJobNameSortColumn;
      const sortDirection =
        sortParams?.sortDirection ?? closedJobsByJobNameSortDirection;
      if (sortColumn) {
        params.sort_column = sortColumn;
        params.sort_direction = sortDirection;
      }
      if (sortParams) {
        setClosedJobsByJobNameSortColumn(sortParams.sortColumn);
        setClosedJobsByJobNameSortDirection(sortParams.sortDirection);
      }

      const response: any = await generateReportByClosedJobs(params);
      const rows = response?.data?.data ?? response?.data;
      if (Array.isArray(rows)) {
        setClosedJobsByJobNameTableData(rows);
      } else {
        setClosedJobsByJobNameTableData([]);
      }
      void loadClosedJobsByJobNameColumnConfigFromApi();
    } catch (error) {
      console.error("Error fetching closed jobs by job name:", error);
      setReport("Error fetching report. Please try again.");
    } finally {
      setClosedJobsByJobNameLoading(false);
    }
  };

  const handleClosedJobsByJobNameSort = (columnKey: string) => {
    const newDirection: SortDirection =
      closedJobsByJobNameSortColumn === columnKey &&
      closedJobsByJobNameSortDirection === "asc"
        ? "desc"
        : "asc";
    getReportByClosedJobsByJobName(filters, {
      sortColumn: columnKey,
      sortDirection: newDirection,
    });
  };

  const getCurrentReportTitle = (): string => {
    switch (currentReportType) {
      case "openJobsByClientGroup":
        return "Open Jobs by Client / Client Group";
      case "openJobsByJobName":
        return "Open Jobs by Job Name";
      case "teamMemberSummary":
        return "Team Member Summary";
      case "closedJobsByClientGroup":
        return "Closed Jobs by Client / Client Group";
      case "closedJobsByJobName":
        return "Closed Jobs by Job Name";
      case "clientGroupUserHours":
        return "Client Group Hours by Team Member";
      default:
        return "Report Generated";
    }
  };

  const getDateFilterText = (): string => {
    if (!filters.startDate && !filters.endDate) return "";

    const start = filters.startDate
      ? formatDateToAustralian(filters.startDate)
      : "Any";
    const end = filters.endDate
      ? formatDateToAustralian(filters.endDate)
      : "Any";

    return `Date Filter: ${start} - ${end}`;
  };

  const dateFilterText = getDateFilterText();
  const isDataTableReportType =
    currentReportType === "openJobsByClientGroup" ||
    currentReportType === "openJobsByJobName" ||
    currentReportType === "closedJobsByClientGroup" ||
    currentReportType === "closedJobsByJobName" ||
    currentReportType === "clientGroupUserHours";

  const reportModalDialogStyle = isDataTableReportType
    ? { maxWidth: "min(96vw, 1800px)", width: "min(96vw, 1800px)" }
    : undefined;

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>Admin Panel</h1>

          <button
            className="logout-button"
            onClick={() => {
              localStorage.clear();
              window.location.href = "/";
            }}
          >
            <span className="logout-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M6 12.5a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v2a.5.5 0 0 1-1 0v-2A1.5 1.5 0 0 1 6.5 2h8A1.5 1.5 0 0 1 16 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 5 12.5v-2a.5.5 0 0 1 1 0v2z"
                />
                <path
                  fillRule="evenodd"
                  d="M.146 8.354a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L1.707 7.5H10.5a.5.5 0 0 1 0 1H1.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3z"
                />
              </svg>
            </span>
            Logout
          </button>
        </div>

        <div className="admin-dashboard">
          <div className="dashboard-header">
            <h2>Welcome to your control center</h2>
            <p>Manage users and generate reports with ease</p>
          </div>

          <div className="admin-cards">
            <div
              className="admin-card"
              data-bs-target="#exampleModalToggle1"
              data-bs-toggle="modal"
            >
              <div className="card-icon user-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0Z" />
                  <path d="M2 1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h9.586a2 2 0 0 1-3 3H2a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1h6.586a4.5 4.5 0 0 0 7.707-7.707A1 1 0 0 0 16 7V1a1 1 0 0 0-1-1H2Zm1.5 1h11a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-7a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </div>
              <div className="card-content">
                <h3>Add User</h3>
                <p>Create new user accounts</p>
              </div>
            </div>

            <div
              className="admin-card"
              data-bs-target="#exampleModalToggle2"
              data-bs-toggle="modal"
            >
              <div className="card-icon delete-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z" />
                </svg>
              </div>
              <div className="card-content">
                <h3>Delete User</h3>
                <p>Remove existing accounts</p>
              </div>
            </div>

            <div
              className="admin-card"
              data-bs-target="#exampleModalToggle3"
              data-bs-toggle="modal"
            >
              <div className="card-icon report-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M0 1.5A1.5 1.5 0 0 1 1.5 0h13A1.5 1.5 0 0 1 16 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13zM1.5 1a.5.5 0 0 0-.5.5v13a.5.5 0 0 0 .5.5H5V9H3.5a.5.5 0 0 1 0-1H5V6.5H3.5a.5.5 0 0 1 0-1H5V4H3.5a.5.5 0 0 1 0-1H5V1H1.5zM10 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 .5-.5zm0 15a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 .5-.5zm-6.207-5h4.765l-.571 2H3.793l.572-2zm-.979 2h5.943l.55-1.931.407-1.432.161-.565a.516.516 0 0 0-.219-.609.508.508 0 0 0-.285-.13H2.812a.5.5 0 0 0-.5.5v3.536c0 .534.208 1.036.586 1.414.418.418.908.586 1.414.586a.5.5 0 0 0 .5-.5v-.035a.488.488 0 0 0-.055-.217l-.161-.565-.121-.426z" />
                </svg>
              </div>
              <div className="card-content">
                <h3>Generate Report</h3>
                <p>Create analytical reports</p>
              </div>
            </div>

            <div
              className="admin-card"
              data-bs-target="#aiReportHubModal"
              data-bs-toggle="modal"
            >
              <div className="card-icon ai-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M7.974 1.73c.172-.123.89-.106 1.128-.033l11.388 11.035c.236.229.236.638 0 .866l-11.388 11c-.238.073-.956.09-1.128-.032-.184-.13-.29-.366-.29-.592V2.323c0-.227.106-.462.29-.592z" />
                  <path d="M9 5.641V10.5A1.5 1.5 0 0 1 7.5 12h-5A1.5 1.5 0 0 1 1 10.5v-5A1.5 1.5 0 0 1 2.5 4h5c.765 0 1.41.56 1.534 1.293.128-.65.239-1.209.421-1.293 1.173-1.253 1.631-1.273 2.186-1.273C12.774 2.727 14 4.422 14 6.5 14 8.773 12.18 10 10.205 10H9z" />
                </svg>
              </div>
              <div className="card-content">
                <h3>AI Report</h3>
                <p>Get AI-powered insights</p>
              </div>
            </div>

            <div
              className="admin-card"
              data-bs-target="#devWorklogModal"
              data-bs-toggle="modal"
            >
              <div className="card-icon worklog-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M2 2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1H2z" />
                  <path d="M2.5 4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5" />
                </svg>
              </div>
              <div className="card-content">
                <h3>WorkLog</h3>
                <p>View developer worklog by date range</p>
              </div>
            </div>

            <div
              className="admin-card"
              role="button"
              tabIndex={0}
              onClick={openUploadSheetsModalFromCard}
              onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                openUploadSheetsModalFromCard();
              }}
            >
              <div className="card-icon tracking-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                  <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
                </svg>
              </div>
              <div className="card-content">
                <h3>Upload Sheets</h3>
                <p>Upload client activity sheets</p>
              </div>
            </div>
            {/* Dashboard Card - new, next to Temporal Activity */}
            <div
              className="admin-card"
              data-bs-target="#dashboardModal"
              data-bs-toggle="modal"
            >
              <div className="card-icon dashboard-icon">
                {/* Dashboard Table Icon - fills 28x28, bold lines */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="none"
                  viewBox="0 0 28 28"
                >
                  <rect
                    x="2"
                    y="4"
                    width="24"
                    height="20"
                    rx="3"
                    fill="#e9ecef"
                    stroke="#adb5bd"
                    strokeWidth="2.5"
                  />
                  <line
                    x1="2"
                    y1="11"
                    x2="26"
                    y2="11"
                    stroke="#adb5bd"
                    strokeWidth="2.5"
                  />
                  <line
                    x1="2"
                    y1="18"
                    x2="26"
                    y2="18"
                    stroke="#adb5bd"
                    strokeWidth="2.5"
                  />
                  <line
                    x1="10"
                    y1="4"
                    x2="10"
                    y2="24"
                    stroke="#adb5bd"
                    strokeWidth="2.5"
                  />
                  <line
                    x1="18"
                    y1="4"
                    x2="18"
                    y2="24"
                    stroke="#adb5bd"
                    strokeWidth="2.5"
                  />
                </svg>
              </div>
              <div className="card-content">
                <h3>Dashboard</h3>
                <p>View client group activity dashboard</p>
              </div>
            </div>
            <div
              className="admin-card"
              onClick={() => {
                navigate("/employee");
              }}
            >
              <div className="card-icon dashboard-icon">
                {/* Dashboard Table Icon - fills 28x28, bold lines */}
                {/* Employee Panel Icon: user group */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="28"
                  fill="none"
                  viewBox="0 0 28 28"
                >
                  <circle
                    cx="9"
                    cy="11"
                    r="4"
                    fill="#e9ecef"
                    stroke="#adb5bd"
                    strokeWidth="2"
                  />
                  <circle
                    cx="19"
                    cy="11"
                    r="4"
                    fill="#e9ecef"
                    stroke="#adb5bd"
                    strokeWidth="2"
                  />
                  <ellipse
                    cx="9"
                    cy="20"
                    rx="6"
                    ry="3"
                    fill="#e9ecef"
                    stroke="#adb5bd"
                    strokeWidth="2"
                  />
                  <ellipse
                    cx="19"
                    cy="20"
                    rx="6"
                    ry="3"
                    fill="#e9ecef"
                    stroke="#adb5bd"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div className="card-content">
                <h3>Employee Panel</h3>
                <p>View employee Panel</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      <div
        className="modal fade user-modal"
        id="exampleModalToggle1"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Add User
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="username">Name</label>
                  <input
                    type="text"
                    id="username"
                    name="name"
                    value={userData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="user_id">User ID</label>
                  <input
                    type="text"
                    id="user_id"
                    name="user_id"
                    value={userData?.user_id}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter user ID"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={userData?.password}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter password"
                  />
                </div>

                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    name="isAdmin"
                    id="isAdmin"
                    checked={userData?.isAdmin}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="isAdmin">Administrator Access</label>
                </div>

                <button type="submit" className="form-button">
                  Create User
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete User Modal */}
      <div
        className="modal fade user-modal delete-user-modal"
        id="exampleModalToggle2"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Delete User
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="warning-text">
                Warning: This action cannot be undone. The user will be
                permanently removed from the system.
              </p>
              <form onSubmit={handleDelete}>
                <div className="form-group">
                  <label htmlFor="delete_user_id">User ID to Delete</label>
                  <input
                    type="text"
                    id="delete_user_id"
                    name="username"
                    value={user_id}
                    onChange={(e) => setUserId(e.target.value)}
                    required
                    placeholder="Enter the User ID to delete"
                  />
                </div>

                <button type="submit" className="form-button">
                  Delete User
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" data-bs-dismiss="modal">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Generate report Modal */}
      <div
        className="modal fade report-modal"
        id="exampleModalToggle3"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Generate Report
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form>
                {/* Employee Reports Section */}
                <div className="report-section">
                  <h5 className="section-title">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      className="me-2"
                    >
                      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                    </svg>
                    Employee Reports
                  </h5>

                  <div className="form-group">
                    <label>Select Employee</label>
                    <Select
                      options={[...(users || [])]
                        .sort()
                        .map((user: any) => ({ value: user, label: user }))}
                      value={
                        selectedUser
                          ? { value: selectedUser, label: selectedUser }
                          : null
                      }
                      onChange={(selectedOption: any) => {
                        setSelectedUser(selectedOption?.value || "");
                      }}
                      placeholder="Select an employee"
                      isClearable
                      isSearchable
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          minHeight: "38px",
                        }),
                      }}
                    />

                    <div className="button-group">
                      <button
                        type="button"
                        className={`report-button ${
                          selectedUser === "" ? "disabled" : ""
                        }`}
                        disabled={selectedUser === ""}
                        data-bs-toggle="modal"
                        data-bs-target="#exampleModalToggle9"
                        onClick={generateWeeklyReport}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          className="me-2"
                        >
                          <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z" />
                          <path
                            fillRule="evenodd"
                            d="M0 10a8 8 0 1 1 15.547 2.661c-.442 1.253-1.845 1.602-2.932 1.25C11.309 13.488 9.475 13 8 13c-1.474 0-3.31.488-4.615.911-1.087.352-2.49.003-2.932-1.25A7.988 7.988 0 0 1 0 10zm8-7a7 7 0 0 0-6.603 9.329c.203.575.923.876 1.68.63C4.397 12.533 6.358 12 8 12s3.604.532 4.923.959c.757.245 1.477-.056 1.68-.631A7 7 0 0 0 8 3z"
                          />
                        </svg>
                        Weekly Report
                      </button>

                      <button
                        type="button"
                        className="report-button"
                        data-bs-toggle="modal"
                        data-bs-target="#exampleModalToggle4"
                        onClick={generateAllUsersWeeklyReport}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                          className="me-2"
                        >
                          <path d="M6 12.5a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v2a.5.5 0 0 1-1 0v-2A1.5 1.5 0 0 1 6.5 2h8A1.5 1.5 0 0 1 16 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 5 12.5v-2a.5.5 0 0 1 1 0v2z" />
                          <path
                            fillRule="evenodd"
                            d="M.146 8.354a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L1.707 7.5H10.5a.5.5 0 0 1 0 1H1.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3z"
                          />
                        </svg>
                        All Users Report
                      </button>
                    </div>
                  </div>
                </div>

                {/* Task & Client Group Reports Section */}
                <div className="report-section">
                  <h5 className="section-title">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      className="me-2"
                    >
                      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
                    </svg>
                    Job & Client Group Reports
                  </h5>

                  <div className="option-grid">
                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#reportByTaskModal"
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M2.5 3.5A.5.5 0 0 1 3 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 1 12V4a.5.5 0 0 1 .5-.5h1zm4 0A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 5 12V4a.5.5 0 0 1 .5-.5h1zm4 0A.5.5 0 0 1 11 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 9 12V4a.5.5 0 0 1 .5-.5h1z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>By Job</h6>
                        <p>Generate reports filtered by specific tasks</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#reportByClientGroupModal"
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-5.784 6A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>By Client Group</h6>
                        <p>Generate reports filtered by client groups</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Advanced Reports Section */}
                <div className="report-section">
                  <h5 className="section-title">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      className="me-2"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
                    </svg>
                    Advanced Reports
                  </h5>

                  <div className="option-grid">
                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModalToggle4"
                      onClick={generateTeamMemberSummaryReport}
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>Team Member Summary</h6>
                        <p>View all team members summary</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModalToggle4"
                      onClick={() => getClientGroupUserHoursReport()}
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm5 1v2h6V3H5zm0 4v2h6V7H5zm0 4v2h6v-2H5zM2 3v2h1V3H2zm0 4v2h1V7H2zm0 4v2h1v-2H2zm11-8v2h1V3h-1zm0 4v2h1V7h-1zm0 4v2h1v-2h-1z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>Client Group Hours by Team Member</h6>
                        <p>Compare logged hours by job and team member</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModalToggle4"
                      onClick={() => getReportByOpenJobsByClientGroup()}
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>Open jobs by Client / Client Group</h6>
                        <p>View all open jobs organized by client group</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModalToggle4"
                      onClick={() => getReportByOpenJobsByJobName()}
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M2.5 3.5A.5.5 0 0 1 3 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 1 12V4a.5.5 0 0 1 .5-.5h1zm4 0A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 5 12V4a.5.5 0 0 1 .5-.5h1zm4 0A.5.5 0 0 1 11 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 9 12V4a.5.5 0 0 1 .5-.5h1z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>Open Jobs by Job Name</h6>
                        <p>View all open jobs organized by job name</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModalToggle4"
                      onClick={getReportByClientGroupActivityRanking}
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M3.5 0a.5.5 0 0 1 .5.5V1h6V.5a.5.5 0 0 1 1 0V1h1a.5.5 0 0 1 .5.5v11a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5v-11A.5.5 0 0 1 .5 1H1V.5a.5.5 0 0 1 .5-.5zM1 2v10h14V2H1z" />
                          <path d="M6.5 4a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h1zm2 0a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h1zm2 0a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h1zm2 0a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h1z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>Client Group Activity Ranking</h6>
                        <p>
                          Rank client groups by activity levels and performance
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModalToggle4"
                      onClick={() => getReportByClosedJobsByClientGroup()}
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 4a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-1 0V4.5A.5.5 0 0 1 8 4zM3.732 5.732a.5.5 0 0 1 .707 0l.915.914a.5.5 0 1 1-.708.708l-.914-.915a.5.5 0 0 1 0-.707zM2 10a.5.5 0 0 1 .5-.5h1.586a.5.5 0 0 1 0 1H2.5A.5.5 0 0 1 2 10zm9.5 0a.5.5 0 0 1 .5-.5h1.5a.5.5 0 0 1 0 1H12a.5.5 0 0 1-.5-.5zm.754-4.246a.389.389 0 0 0-.527-.02L7.547 9.31a.91.91 0 1 0 1.302 1.258l3.434-4.297a.389.389 0 0 0-.029-.518z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>Closed Jobs by Client / Client Group</h6>
                        <p>View all closed jobs organized by client group</p>
                      </div>
                    </button>

                    <button
                      type="button"
                      className="option-card"
                      data-bs-toggle="modal"
                      data-bs-target="#exampleModalToggle4"
                      onClick={() => getReportByClosedJobsByJobName()}
                    >
                      <div className="option-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M2.5 3.5A.5.5 0 0 1 3 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 1 12V4a.5.5 0 0 1 .5-.5h1zm4 0A.5.5 0 0 1 7 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 5 12V4a.5.5 0 0 1 .5-.5h1zm4 0A.5.5 0 0 1 11 4v8a.5.5 0 0 1-.5.5h-1A.5.5 0 0 1 9 12V4a.5.5 0 0 1 .5-.5h1z" />
                        </svg>
                      </div>
                      <div className="option-content">
                        <h6>Closed Jobs by Job Name</h6>
                        <p>View all closed jobs organized by job name</p>
                      </div>
                    </button>
                  </div>
                </div>

                <button type="submit" className="form-button mt-4">
                  Done
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" data-bs-dismiss="modal">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade modal-xl"
        id="exampleModalToggle4"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div
          className={`modal-dialog modal-dialog-centered ${
            isDataTableReportType ? "modal-xxl" : ""
          }`}
          style={reportModalDialogStyle}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                {getCurrentReportTitle()}
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            {/* Conditionally show filters only for certain report types */}
            {(currentReportType === "openJobsByClientGroup" ||
              currentReportType === "openJobsByJobName" ||
              currentReportType === "teamMemberSummary" ||
              currentReportType === "closedJobsByClientGroup" ||
              currentReportType === "closedJobsByJobName" ||
              currentReportType === "clientGroupUserHours") && (
              <div className="modal-body border-bottom">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Filters</h6>

                  {/* View Toggle */}
                  {currentReportType === "teamMemberSummary" && (
                    <div className="d-flex align-items-center gap-2">
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "140px" }}
                        value={filters.tabular_view}
                        onChange={(e) =>
                          handleDisplayTypeChange(e.target.value)
                        }
                      >
                        <option value="normal">Normal View</option>
                        <option value="tabular">Tabular View</option>
                      </select>
                    </div>
                  )}

                  {currentReportType === "openJobsByClientGroup" && (
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0">View</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "140px" }}
                        value={openJobsByClientGroupViewMode}
                        onChange={(e) =>
                          handleOpenJobsByClientGroupViewModeChange(
                            e.target.value as ReportViewMode,
                          )
                        }
                      >
                        <option value="table">Table</option>
                        <option value="cards">Cards</option>
                      </select>
                    </div>
                  )}

                  {currentReportType === "closedJobsByClientGroup" && (
                    <div className="d-flex align-items-center gap-2">
                      <label className="form-label mb-0">View</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "140px" }}
                        value={closedJobsByClientGroupViewMode}
                        onChange={(e) =>
                          handleClosedJobsByClientGroupViewModeChange(
                            e.target.value as ReportViewMode,
                          )
                        }
                      >
                        <option value="table">Table</option>
                        <option value="cards">Cards</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="row g-3">
                  {currentReportType === "clientGroupUserHours" && (
                    <div className="col-12">
                      <label htmlFor="clientGroupHoursStatus" className="form-label">
                        Job Status
                      </label>
                      <select
                        id="clientGroupHoursStatus"
                        className="form-select"
                        value={String(clientGroupHoursClosed)}
                        onChange={(e) =>
                          setClientGroupHoursClosed(e.target.value === "true")
                        }
                      >
                        <option value="false">Open jobs</option>
                        <option value="true">Closed jobs</option>
                      </select>
                    </div>
                  )}

                  {/* Start Date Filter */}
                  <div className="col-md-6">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={filters.startDate}
                      onChange={(e) =>
                        handleStartDateChangeClientJob(e.target.value)
                      }
                    />
                  </div>

                  {/* End Date Filter */}
                  <div className="col-md-6">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={filters.endDate}
                      onChange={(e) => handleEndDateChange(e.target.value)}
                    />
                  </div>

                  {/* Search - Open jobs and Closed jobs reports */}
                  {(currentReportType === "openJobsByClientGroup" ||
                    currentReportType === "openJobsByJobName" ||
                    currentReportType === "closedJobsByClientGroup" ||
                    currentReportType === "closedJobsByJobName" ||
                    currentReportType === "clientGroupUserHours") && (
                    <div className="col-12">
                      <label htmlFor="reportSearch" className="form-label">
                        Search
                      </label>
                      <input
                        type="text"
                        id="reportSearch"
                        className="form-control"
                        placeholder={
                          currentReportType === "openJobsByJobName" ||
                          currentReportType === "closedJobsByJobName"
                            ? "Search job name, client group, project..."
                            : currentReportType === "clientGroupUserHours"
                              ? "Search client group..."
                            : "Search client group, project, status..."
                        }
                        value={filters.search}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            search: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}

                  {/* Apply Filters Button */}
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <button
                        className="btn btn-primary"
                        onClick={() => applyFilters()}
                        disabled={
                          currentReportType !== "openJobsByClientGroup" &&
                          currentReportType !== "openJobsByJobName" &&
                          currentReportType !== "closedJobsByClientGroup" &&
                          currentReportType !== "closedJobsByJobName" &&
                          currentReportType !== "clientGroupUserHours" &&
                          !filters.startDate &&
                          !filters.endDate &&
                          filters.tabular_view === "normal"
                        }
                      >
                        Apply Filters & Refresh
                      </button>

                      {/* Clear Filters Button */}
                      <button
                        className="btn btn-outline-secondary"
                        onClick={handleClearFilters}
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              className="modal-body"
              style={{
                maxHeight: "60vh",
                overflowY: "auto",
              }}
            >
              {/* Open jobs by client group: table view */}
              {currentReportType === "openJobsByClientGroup" && (
                <>
                  {openJobsLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "100px" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!openJobsLoading &&
                    openJobsByClientGroupViewMode === "table" &&
                    openJobsTableData !== null && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                          {dateFilterText && (
                            <div className="fw-semibold text-primary fs-6">
                              {dateFilterText}
                            </div>
                          )}
                          <div className="ms-auto">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              id="openJobsColumnsButton"
                              onClick={() => void handleOpenOpenJobsColumnPicker()}
                            >
                              Columns
                            </button>
                          </div>
                        </div>
                        <DataTable<OpenJobsByClientGroupRow>
                          data={openJobsTableData}
                          columns={openJobsColumnsVisible}
                          sortColumn={openJobsSortColumn}
                          sortDirection={openJobsSortDirection}
                          onSort={handleOpenJobsSort}
                          emptyMessage="No data for the selected filters."
                          maxHeight="50vh"
                          getRowKey={(row) => row.taskId}
                          onRowClick={(row) =>
                            setReportJobDetailRow(
                              row as unknown as Record<string, unknown>,
                            )
                          }
                        />
                      </>
                    )}
                  {!openJobsLoading &&
                    openJobsByClientGroupViewMode === "cards" &&
                    openJobsGroupedCardData !== null && (
                      <div className="d-flex flex-column gap-3">
                        {dateFilterText && (
                          <div className="fw-semibold text-primary fs-6">
                            {dateFilterText}
                          </div>
                        )}
                        {Object.keys(openJobsGroupedCardData).length === 0 ? (
                          <div className="text-muted">
                            No data for the selected filters.
                          </div>
                        ) : (
                          Object.entries(openJobsGroupedCardData).map(
                            ([clientGroup, group]) => {
                              const isExpanded =
                                openJobsExpandedGroups[clientGroup] !== false;
                              return (
                                <div
                                  key={clientGroup}
                                  className="card border-0 shadow-sm"
                                >
                                  <button
                                    type="button"
                                    className="btn btn-light text-start w-100 d-flex justify-content-between align-items-center p-3"
                                    onClick={() =>
                                      setOpenJobsExpandedGroups((prev) => ({
                                        ...prev,
                                        [clientGroup]: !isExpanded,
                                      }))
                                    }
                                  >
                                    <div>
                                      <div className="fw-bold fs-5">
                                        {clientGroup}
                                      </div>
                                      <div className="fw-semibold fs-6">
                                        Total Hours:{" "}
                                        {formatHours(
                                          group.totalHoursLoggedThisPeriodByClientGroup,
                                        )}
                                      </div>
                                    </div>
                                    <span className="fw-bold">
                                      {isExpanded ? "−" : "+"}
                                    </span>
                                  </button>

                                  {isExpanded && (
                                    <div className="card-body pt-2">
                                      <div className="d-flex flex-column gap-2">
                                        {(group.jobs ?? []).map((job) => (
                                          <div
                                            key={job.taskId}
                                            className="card border"
                                          >
                                            <div className="card-body py-2 px-3">
                                              <div className="fw-semibold">
                                                {job.projectId ||
                                                  job.jobName ||
                                                  `Task #${job.taskId}`}
                                              </div>
                                              <div className="small text-muted mt-1 mb-2">
                                                {job.projectDesc || "-"}
                                              </div>
                                              <div className="row g-2">
                                                {Object.entries(job)
                                                  .filter(
                                                    ([k]) =>
                                                      k !== "statusBoolean",
                                                  )
                                                  .map(([key, value]) => (
                                                    <div
                                                      key={`${job.taskId}-${key}`}
                                                      className="col-12 col-md-6"
                                                    >
                                                      <div className="small">
                                                        <strong>
                                                          {formatFieldLabel(
                                                            key,
                                                          )}
                                                          :
                                                        </strong>{" "}
                                                        {formatFieldValue(
                                                          key,
                                                          value,
                                                          job as unknown as Record<
                                                            string,
                                                            unknown
                                                          >,
                                                        )}
                                                      </div>
                                                    </div>
                                                  ))}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            },
                          )
                        )}
                      </div>
                    )}
                  {!openJobsLoading && openJobsTableData === null && report && (
                    <div className="text-danger">{report}</div>
                  )}
                </>
              )}

              {/* Open jobs by job name: table view */}
              {currentReportType === "openJobsByJobName" && (
                <>
                  {openJobsByJobNameLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "100px" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!openJobsByJobNameLoading &&
                    openJobsByJobNameTableData !== null && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                          {dateFilterText && (
                            <div className="fw-semibold text-primary fs-6">
                              {dateFilterText}
                            </div>
                          )}
                          <div className="ms-auto">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              id="openJobsByJobNameColumnsButton"
                              onClick={() =>
                                void handleOpenOpenJobsByJobNameColumnPicker()
                              }
                            >
                              Columns
                            </button>
                          </div>
                        </div>
                        <DataTable<OpenJobsByJobNameRow>
                          data={openJobsByJobNameTableData}
                          columns={openJobsByJobNameColumnsVisible}
                          sortColumn={openJobsByJobNameSortColumn}
                          sortDirection={openJobsByJobNameSortDirection}
                          onSort={handleOpenJobsByJobNameSort}
                          emptyMessage="No data for the selected filters."
                          maxHeight="50vh"
                          getRowKey={(row) => row.taskId}
                          onRowClick={(row) =>
                            setReportJobDetailRow(
                              row as unknown as Record<string, unknown>,
                            )
                          }
                        />
                      </>
                    )}
                  {!openJobsByJobNameLoading &&
                    openJobsByJobNameTableData === null &&
                    report && <div className="text-danger">{report}</div>}
                </>
              )}

              {/* Closed jobs by client group: table view */}
              {currentReportType === "closedJobsByClientGroup" && (
                <>
                  {closedJobsByClientGroupLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "100px" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!closedJobsByClientGroupLoading &&
                    closedJobsByClientGroupViewMode === "table" &&
                    closedJobsByClientGroupTableData !== null && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                          {dateFilterText && (
                            <div className="fw-semibold text-primary fs-6">
                              {dateFilterText}
                            </div>
                          )}
                          <div className="ms-auto">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              id="closedJobsByClientGroupColumnsButton"
                              onClick={() =>
                                void handleOpenClosedJobsByClientGroupColumnPicker()
                              }
                            >
                              Columns
                            </button>
                          </div>
                        </div>
                        <DataTable<ClosedJobsRow>
                          data={closedJobsByClientGroupTableData}
                          columns={closedJobsByClientGroupColumnsVisible}
                          sortColumn={closedJobsByClientGroupSortColumn}
                          sortDirection={closedJobsByClientGroupSortDirection}
                          onSort={handleClosedJobsByClientGroupSort}
                          emptyMessage="No data for the selected filters."
                          maxHeight="50vh"
                          getRowKey={(row) => row.taskId}
                          onRowClick={(row) =>
                            setReportJobDetailRow(
                              row as unknown as Record<string, unknown>,
                            )
                          }
                        />
                      </>
                    )}
                  {!closedJobsByClientGroupLoading &&
                    closedJobsByClientGroupViewMode === "cards" &&
                    closedJobsByClientGroupGroupedCardData !== null && (
                      <div className="d-flex flex-column gap-3">
                        {dateFilterText && (
                          <div className="fw-semibold text-primary fs-6">
                            {dateFilterText}
                          </div>
                        )}
                        {Object.keys(closedJobsByClientGroupGroupedCardData)
                          .length === 0 ? (
                          <div className="text-muted">
                            No data for the selected filters.
                          </div>
                        ) : (
                          Object.entries(
                            closedJobsByClientGroupGroupedCardData,
                          ).map(([clientGroup, group]) => {
                            const isExpanded =
                              closedJobsExpandedGroups[clientGroup] !== false;
                            return (
                              <div
                                key={clientGroup}
                                className="card border-0 shadow-sm"
                              >
                                <button
                                  type="button"
                                  className="btn btn-light text-start w-100 d-flex justify-content-between align-items-center p-3"
                                  onClick={() =>
                                    setClosedJobsExpandedGroups((prev) => ({
                                      ...prev,
                                      [clientGroup]: !isExpanded,
                                    }))
                                  }
                                >
                                  <div>
                                    <div className="fw-bold fs-5">
                                      {clientGroup}
                                    </div>
                                    <div className="fw-semibold fs-6">
                                      Total Hours:{" "}
                                      {formatHours(
                                        group.totalHoursLoggedThisPeriodByClientGroup,
                                      )}
                                    </div>
                                  </div>
                                  <span className="fw-bold">
                                    {isExpanded ? "−" : "+"}
                                  </span>
                                </button>

                                {isExpanded && (
                                  <div className="card-body pt-2">
                                    <div className="d-flex flex-column gap-2">
                                      {(group.jobs ?? []).map((job) => (
                                        <div
                                          key={job.taskId}
                                          className="card border"
                                        >
                                          <div className="card-body py-2 px-3">
                                            <div className="fw-semibold">
                                              {job.projectId ||
                                                job.jobName ||
                                                `Task #${job.taskId}`}
                                            </div>
                                            <div className="small text-muted mt-1 mb-2">
                                              {job.projectDesc || "-"}
                                            </div>
                                            <div className="row g-2">
                                              {Object.entries(job)
                                                .filter(
                                                  ([k]) =>
                                                    k !== "statusBoolean",
                                                )
                                                .map(([key, value]) => (
                                                  <div
                                                    key={`${job.taskId}-${key}`}
                                                    className="col-12 col-md-6"
                                                  >
                                                    <div className="small">
                                                      <strong>
                                                        {formatFieldLabel(key)}:
                                                      </strong>{" "}
                                                      {formatFieldValue(
                                                        key,
                                                        value,
                                                        job as unknown as Record<
                                                          string,
                                                          unknown
                                                        >,
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  {!closedJobsByClientGroupLoading &&
                    closedJobsByClientGroupTableData === null &&
                    report && <div className="text-danger">{report}</div>}
                </>
              )}

              {/* Closed jobs by job name: table view */}
              {currentReportType === "closedJobsByJobName" && (
                <>
                  {closedJobsByJobNameLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "100px" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!closedJobsByJobNameLoading &&
                    closedJobsByJobNameTableData !== null && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                          {dateFilterText && (
                            <div className="fw-semibold text-primary fs-6">
                              {dateFilterText}
                            </div>
                          )}
                          <div className="ms-auto">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              id="closedJobsByJobNameColumnsButton"
                              onClick={() =>
                                void handleOpenClosedJobsByJobNameColumnPicker()
                              }
                            >
                              Columns
                            </button>
                          </div>
                        </div>
                        <DataTable<ClosedJobsRow>
                          data={closedJobsByJobNameTableData}
                          columns={closedJobsByJobNameColumnsVisible}
                          sortColumn={closedJobsByJobNameSortColumn}
                          sortDirection={closedJobsByJobNameSortDirection}
                          onSort={handleClosedJobsByJobNameSort}
                          emptyMessage="No data for the selected filters."
                          maxHeight="50vh"
                          getRowKey={(row) => row.taskId}
                          onRowClick={(row) =>
                            setReportJobDetailRow(
                              row as unknown as Record<string, unknown>,
                            )
                          }
                        />
                      </>
                    )}
                  {!closedJobsByJobNameLoading &&
                    closedJobsByJobNameTableData === null &&
                    report && <div className="text-danger">{report}</div>}
                </>
              )}

              {currentReportType === "clientGroupUserHours" && (
                <>
                  {clientGroupHoursLoading && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "100px" }}
                    >
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!clientGroupHoursLoading && clientGroupHoursData && (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="fw-semibold text-primary">
                          {clientGroupHoursData.closed ? "Closed" : "Open"} jobs
                          {dateFilterText ? ` · ${dateFilterText}` : ""}
                        </div>
                        <div className="text-muted small">
                          {clientGroupHoursData.totalJobs} jobs
                        </div>
                      </div>
                      <div className="table-responsive client-group-hours-table-wrap">
                        <table className="table table-bordered table-hover align-middle mb-0 client-group-hours-table">
                          <thead>
                            <tr>
                              <th style={{ minWidth: 160 }}>Client Group</th>
                              <th style={{ minWidth: 220 }}>Job</th>
                              {clientGroupHoursData.users.map((member) => (
                                <th
                                  key={member.userId}
                                  className="text-end"
                                  style={{ minWidth: 110 }}
                                  title={member.userId}
                                >
                                  {member.name}
                                </th>
                              ))}
                              <th className="text-end" style={{ minWidth: 100 }}>
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientGroupHoursData.data.flatMap((group) => [
                              ...group.jobs.map((job, index) => (
                                <tr key={`job-${job.taskId}`}>
                                  <td>{index === 0 ? group.clientGroup : ""}</td>
                                  <td>
                                    <div className="fw-semibold">
                                      {job.projectId || `Task #${job.taskId}`}
                                    </div>
                                    {job.projectDesc && (
                                      <div className="small text-muted">
                                        {job.projectDesc}
                                      </div>
                                    )}
                                  </td>
                                  {clientGroupHoursData.users.map((member) => (
                                    <td
                                      key={`${job.taskId}-${member.userId}`}
                                      className="text-end"
                                    >
                                      {formatHours(
                                        job.userHours[member.userId] ?? 0,
                                      )}{" "}
                                      hrs
                                    </td>
                                  ))}
                                  <td className="text-end fw-semibold">
                                    {formatHours(job.totalHours)} hrs
                                  </td>
                                </tr>
                              )),
                              <tr
                                key={`total-${group.clientGroup}`}
                                className="table-light fw-bold"
                              >
                                <td colSpan={2}>{group.clientGroup} Total</td>
                                {clientGroupHoursData.users.map((member) => (
                                  <td
                                    key={`total-${group.clientGroup}-${member.userId}`}
                                    className="text-end"
                                  >
                                    {formatHours(
                                      group.userHours[member.userId] ?? 0,
                                    )}{" "}
                                    hrs
                                  </td>
                                ))}
                                <td className="text-end">
                                  {formatHours(group.totalHours)} hrs
                                </td>
                              </tr>,
                            ])}
                            {clientGroupHoursData.data.length === 0 && (
                              <tr>
                                <td
                                  colSpan={clientGroupHoursData.users.length + 3}
                                  className="text-center text-muted py-4"
                                >
                                  No jobs found for the selected filters.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  {!clientGroupHoursLoading &&
                    clientGroupHoursData === null &&
                    report && <div className="text-danger">{report}</div>}
                </>
              )}

              {/* Other reports: HTML / string content */}
              {currentReportType !== "openJobsByClientGroup" &&
                currentReportType !== "openJobsByJobName" &&
                currentReportType !== "closedJobsByClientGroup" &&
                currentReportType !== "closedJobsByJobName" &&
                currentReportType !== "clientGroupUserHours" && (
                  <>
                    {!report && (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "100px" }}
                      >
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                    {report && (
                      <div>
                        <div dangerouslySetInnerHTML={{ __html: report }}></div>
                      </div>
                    )}
                  </>
                )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => {
                  setReport("");
                  setOpenJobsTableData(null);
                  setOpenJobsByJobNameTableData(null);
                  setClosedJobsByClientGroupTableData(null);
                  setClosedJobsByJobNameTableData(null);
                  setClientGroupHoursData(null);
                  setReportJobDetailRow(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {reportJobDetailRow && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1080 }}
            aria-hidden
            onClick={() => setReportJobDetailRow(null)}
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="adminReportJobDetailTitle"
            style={{ zIndex: 1085 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h2
                    className="modal-title fs-5"
                    id="adminReportJobDetailTitle"
                  >
                    Job row details
                  </h2>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setReportJobDetailRow(null)}
                  />
                </div>
                <div className="modal-body">
                  <p className="small text-muted mb-3">
                    Full GitHub URLs are in the box below so you can copy them (for
                    example to adjust links in the employee job editor).
                  </p>
                  <div className="row g-2 mb-4">
                    {Object.entries(reportJobDetailRow)
                      .filter(
                        ([k]) =>
                          k !== "githubLink" &&
                          k !== "github_link" &&
                          k !== "githubLinks" &&
                          k !== "github_links" &&
                          k !== "statusBoolean",
                      )
                      .map(([key, value]) => (
                        <div key={key} className="col-12 col-md-6">
                          <div className="small">
                            <strong>{formatFieldLabel(key)}:</strong>{" "}
                            <span className="text-break">
                              {formatFieldValue(
                                key,
                                value,
                                reportJobDetailRow,
                              )}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                  <h3 className="h6 mb-2">GitHub links (full)</h3>
                  {githubLinksFromRow(reportJobDetailRow).length > 0 ? (
                    <div>
                      <textarea
                        readOnly
                        className="form-control font-monospace small"
                        rows={Math.min(
                          12,
                          githubLinksFromRow(reportJobDetailRow).length + 3,
                        )}
                        value={githubLinksFromRow(reportJobDetailRow).join(
                          "\n",
                        )}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() => {
                          void navigator.clipboard?.writeText(
                            githubLinksFromRow(reportJobDetailRow).join("\n"),
                          );
                        }}
                      >
                        Copy all
                      </button>
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No GitHub links</p>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setReportJobDetailRow(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <ReportColumnPickerModal
        open={openJobsColumnPickerOpen}
        titleId="openJobsByClientGroupColumnConfigTitle"
        loading={openJobsColumnPickerLoading}
        saveLoading={openJobsColumnSaveLoading}
        columnDefs={OPEN_JOBS_BY_CLIENT_GROUP_COLUMNS}
        draft={openJobsColumnPickerDraft}
        onToggle={toggleOpenJobsColumnPickerDraft}
        onClose={handleCloseOpenJobsColumnPicker}
        onSave={handleSaveOpenJobsColumnConfig}
      />

      <ReportColumnPickerModal
        open={openJobsByJobNameColumnPickerOpen}
        titleId="openJobsByJobNameColumnConfigTitle"
        loading={openJobsByJobNameColumnPickerLoading}
        saveLoading={openJobsByJobNameColumnSaveLoading}
        columnDefs={OPEN_JOBS_BY_JOB_NAME_COLUMNS}
        draft={openJobsByJobNameColumnPickerDraft}
        onToggle={toggleOpenJobsByJobNameColumnPickerDraft}
        onClose={handleCloseOpenJobsByJobNameColumnPicker}
        onSave={handleSaveOpenJobsByJobNameColumnConfig}
      />

      <ReportColumnPickerModal
        open={closedJobsByClientGroupColumnPickerOpen}
        titleId="closedJobsByClientGroupColumnConfigTitle"
        loading={closedJobsByClientGroupColumnPickerLoading}
        saveLoading={closedJobsByClientGroupColumnSaveLoading}
        columnDefs={CLOSED_JOBS_BY_CLIENT_GROUP_COLUMNS}
        draft={closedJobsByClientGroupColumnPickerDraft}
        onToggle={toggleClosedJobsByClientGroupColumnPickerDraft}
        onClose={handleCloseClosedJobsByClientGroupColumnPicker}
        onSave={handleSaveClosedJobsByClientGroupColumnConfig}
      />

      <ReportColumnPickerModal
        open={closedJobsByJobNameColumnPickerOpen}
        titleId="closedJobsByJobNameColumnConfigTitle"
        loading={closedJobsByJobNameColumnPickerLoading}
        saveLoading={closedJobsByJobNameColumnSaveLoading}
        columnDefs={CLOSED_JOBS_BY_JOB_NAME_COLUMNS}
        draft={closedJobsByJobNameColumnPickerDraft}
        onToggle={toggleClosedJobsByJobNameColumnPickerDraft}
        onClose={handleCloseClosedJobsByJobNameColumnPicker}
        onSave={handleSaveClosedJobsByJobNameColumnConfig}
      />

      <div
        className="modal fade"
        id="exampleModalToggle5"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Report Generated
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {/* Render report if it exists */}
              {/* {report && <div dangerouslySetInnerHTML={{ __html: report }}></div>} */}

              {/* Render allReport if it exists and is not empty */}
              {allReport && allReport.length !== 0 && (
                <div>
                  {allReport.map((item: any, index: number) => (
                    <div key={index}>
                      <h3 className="fw-bold">{"User Id: " + item.id}</h3>

                      <div
                        dangerouslySetInnerHTML={{ __html: item?.reports }}
                      ></div>

                      <hr></hr>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                data-bs-dismiss="modal"
                onClick={() => setReport("")}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AI Reports hub — choose employee chat vs project GitHub report */}
      <div
        className="modal fade ai-report-hub-modal"
        id="aiReportHubModal"
        aria-hidden="true"
        aria-labelledby="aiReportHubTitle"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content overflow-hidden border-0 shadow">
            <div className="modal-header ai-report-hub-modal-header align-items-start">
              <div className="pe-3">
                <h1 className="modal-title fs-4 fw-semibold mb-0" id="aiReportHubTitle">
                  AI reports
                </h1>
                <p className="ai-report-hub-modal-subtitle small mb-0 mt-2">
                  Pick a report type to continue.
                </p>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white mt-1"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body ai-report-hub-modal-body">
              <div className="row g-4">
                <div className="col-md-6">
                  <div
                    role="button"
                    tabIndex={0}
                    className="ai-report-hub-tile"
                    onClick={() => {
                      aiHubOpeningChildRef.current = "employee";
                      const hub = document.getElementById("aiReportHubModal");
                      if (hub) Modal.getOrCreateInstance(hub).hide();
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      (e.currentTarget as HTMLDivElement).click();
                    }}
                  >
                    <div className="ai-report-hub-tile-icon" aria-hidden>
                      <i className="fas fa-user" />
                    </div>
                    <h2 className="ai-report-hub-tile-title">By employee</h2>
                    <p className="ai-report-hub-tile-desc">
                      Ask questions about a team member&apos;s work for a date range
                      (same experience as before).
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div
                    role="button"
                    tabIndex={0}
                    className="ai-report-hub-tile ai-report-hub-tile--accent"
                    onClick={() => {
                      aiHubOpeningChildRef.current = "github";
                      const hub = document.getElementById("aiReportHubModal");
                      if (hub) Modal.getOrCreateInstance(hub).hide();
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      (e.currentTarget as HTMLDivElement).click();
                    }}
                  >
                    <div className="ai-report-hub-tile-icon" aria-hidden>
                      <i className="fas fa-folder-open" />
                    </div>
                    <h2 className="ai-report-hub-tile-title">By project</h2>
                    <p className="ai-report-hub-tile-desc">
                      GitHub activity summary for a project name and date range.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade ai-modal"
        id="aiReportGithubProjectModal"
        aria-hidden="true"
        aria-labelledby="aiReportGithubProjectTitle"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h1
                className="modal-title fs-5"
                id="aiReportGithubProjectTitle"
              >
                GitHub report by project
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {aiGithubProjectResult ? (
                <div>
                  {aiGithubProjectResult?.aiSummaryError ? (
                    <div className="alert alert-warning" role="alert">
                      {String(aiGithubProjectResult.aiSummaryError)}
                    </div>
                  ) : null}
                  {typeof aiGithubProjectResult === "string" ? (
                    <pre
                      className="small bg-light p-3 rounded border mb-0"
                      style={{ maxHeight: "min(60vh, 28rem)", overflow: "auto" }}
                    >
                      {aiGithubProjectResult}
                    </pre>
                  ) : typeof aiGithubProjectResult?.aiSummary === "string" &&
                  aiGithubProjectResult.aiSummary.trim() !== "" ? (
                    <div
                      className="markdown-body border rounded-3 p-3 bg-white"
                      style={{ maxHeight: "min(60vh, 28rem)", overflowY: "auto" }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {tidyMarkdown(aiGithubProjectResult.aiSummary)}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <pre
                      className="small bg-light p-3 rounded border mb-0"
                      style={{ maxHeight: "min(60vh, 28rem)", overflow: "auto" }}
                    >
                      {JSON.stringify(aiGithubProjectResult, null, 2)}
                    </pre>
                  )}
                  <div className="d-flex gap-2 flex-wrap mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setAiGithubProjectResult(null);
                        setAiGithubProjectError("");
                      }}
                    >
                      New report
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      data-bs-dismiss="modal"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={(e) => void submitAiGithubProjectReport(e)}>
                  <div className="mb-3">
                    <label
                      className="form-label"
                      htmlFor="aiGithubProjectSelect"
                    >
                      Project
                    </label>
                    <Select<{ value: string; label: string }>
                      inputId="aiGithubProjectSelect"
                      classNamePrefix="ai-github-project-select"
                      options={githubProjectPickerOptions.map((p) => ({
                        value: p,
                        label: p,
                      }))}
                      value={
                        aiGithubProjectName
                          ? {
                              value: aiGithubProjectName,
                              label: aiGithubProjectName,
                            }
                          : null
                      }
                      onChange={(selectedOption: any) => {
                        setAiGithubProjectName(selectedOption?.value || "");
                      }}
                      placeholder="Select a project"
                      isClearable
                      isSearchable
                      noOptionsMessage={() =>
                        githubProjectPickerOptions.length === 0
                          ? "No projects loaded yet"
                          : "No matching project"
                      }
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          minHeight: "42px",
                        }),
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="aiGithubProjectStart">
                      Start date
                    </label>
                    <DatePicker
                      id="aiGithubProjectStart"
                      className="form-control w-100"
                      selected={aiGithubProjectStart}
                      onChange={(d: Date | null) =>
                        d && setAiGithubProjectStart(d)
                      }
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-MM-yyyy"
                      locale="en-AU"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="aiGithubProjectEnd">
                      End date
                    </label>
                    <DatePicker
                      id="aiGithubProjectEnd"
                      className="form-control w-100"
                      selected={aiGithubProjectEnd}
                      onChange={(d: Date | null) => d && setAiGithubProjectEnd(d)}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-MM-yyyy"
                      locale="en-AU"
                    />
                  </div>
                  {aiGithubProjectError ? (
                    <div className="alert alert-danger py-2" role="alert">
                      {aiGithubProjectError}
                    </div>
                  ) : null}
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={aiGithubProjectLoading}
                    >
                      {aiGithubProjectLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden
                          />
                          Generating…
                        </>
                      ) : (
                        "Generate report"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI report modal (by employee — chat) */}
      <div
        className="modal fade ai-modal"
        id="exampleModalToggle6"
        aria-hidden="true"
        aria-labelledby="exampleModalToggleLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                AI Report
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {AIReportLoading ? (
                // Loader
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "300px" }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                // Content when not loading
                <>
                  <div style={{ marginRight: "1rem", minWidth: "250px" }}>
                    <Select
                      options={[...(users || [])]
                        .sort()
                        .map((user: any) => ({ value: user, label: user }))}
                      value={
                        selectedUser
                          ? { value: selectedUser, label: selectedUser }
                          : null
                      }
                      onChange={(selectedOption: any) => {
                        setSelectedUser(selectedOption?.value || "");
                      }}
                      placeholder="Employee:"
                      isClearable
                      isSearchable
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          minHeight: "38px",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="weekStart" className="form-label">
                      Date Start:
                    </label>
                    <DatePicker
                      id="weekStart"
                      className="form-control w-100"
                      selected={startDate}
                      onChange={changeStartDate}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-MM-yyyy"
                      locale="en-AU"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="weekEnd" className="form-label">
                      Date End:
                    </label>
                    <DatePicker
                      id="weekEnd"
                      className="form-control w-100"
                      selected={endDate}
                      onChange={changeEndDate}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-MM-yyyy"
                      locale="en-AU"
                    />
                  </div>

                  {error && <div className="text-danger mb-3">{error}</div>}

                  <div className="chat-container mt-3">
                    {history.map((item: any, index: number) => {
                      if (item.type === "query") {
                        return (
                          <div key={index} className="message user-message">
                            <div className="message-content">
                              <p>{item.query}</p>
                              <span className="status">
                                <svg
                                  width="16px"
                                  height="16px"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M23.228 8.01785C23.6186 7.62741 23.6187 6.99424 23.2283 6.60363L22.5213 5.89638C22.1309 5.50577 21.4977 5.50563 21.1071 5.89607L10.0862 16.9122C9.69563 17.3027 9.6955 17.9359 10.0859 18.3265L10.7929 19.0337C11.1833 19.4243 11.8165 19.4245 12.2071 19.034L23.228 8.01785Z"
                                    fill="#008b02"
                                  />
                                  <path
                                    d="M17.2285 8.01777C17.619 7.62724 17.619 6.99408 17.2285 6.60356L16.5214 5.89645C16.1309 5.50592 15.4977 5.50592 15.1072 5.89645L5.54542 15.4582L2.76773 12.6805C2.37721 12.29 1.74404 12.29 1.35352 12.6805L0.646409 13.3876C0.255884 13.7782 0.255885 14.4113 0.646409 14.8019L4.83831 18.9938C5.22883 19.3843 5.862 19.3843 6.25252 18.9938L17.2285 8.01777Z"
                                    fill="#008b02"
                                  />
                                </svg>
                                seen &nbsp;
                              </span>
                            </div>
                          </div>
                        );
                      } else if (item.type === "response") {
                        return (
                          <div key={index} className="message ai-message">
                            <div className="message-content">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {item.response}
                              </ReactMarkdown>
                              <span className="status">
                                <svg
                                  width="16px"
                                  height="16px"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M23.228 8.01785C23.6186 7.62741 23.6187 6.99424 23.2283 6.60363L22.5213 5.89638C22.1309 5.50577 21.4977 5.50563 21.1071 5.89607L10.0862 16.9122C9.69563 17.3027 9.6955 17.9359 10.0859 18.3265L10.7929 19.0337C11.1833 19.4243 11.8165 19.4245 12.2071 19.034L23.228 8.01785Z"
                                    fill="#008b02"
                                  />
                                  <path
                                    d="M17.2285 8.01777C17.619 7.62724 17.619 6.99408 17.2285 6.60356L16.5214 5.89645C16.1309 5.50592 15.4977 5.50592 15.1072 5.89645L5.54542 15.4582L2.76773 12.6805C2.37721 12.29 1.74404 12.29 1.35352 12.6805L0.646409 13.3876C0.255884 13.7782 0.255885 14.4113 0.646409 14.8019L4.83831 18.9938C5.22883 19.3843 5.862 19.3843 6.25252 18.9938L17.2285 8.01777Z"
                                    fill="#008b02"
                                  />
                                </svg>
                                &nbsp; seen
                              </span>
                            </div>
                          </div>
                        );
                      } else if (item.type === "loading") {
                        return (
                          <div key={index} className="message loading">
                            <div className="typing-loader">
                              <div className="d-flex justify-content-center align-items-center">
                                <div
                                  className="spinner-border text-dark"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      } else if (item.type === "error") {
                        return (
                          <div key={index} className="message error">
                            <div className="message-content">
                              <p>{item.error}</p>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <div className="input-container">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      className="message-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          sendMessage();
                        }
                      }}
                    />
                    <button
                      className="send-button"
                      onClick={sendMessage}
                      disabled={isLoading || !newMessage.trim()}
                    >
                      Send
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="clear-button" onClick={() => setHistory([])}>
                Clear Chat
              </button>
              <button className="cancel-button" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Modal*/}
      <div
        className="modal fade"
        id="weeklyReport"
        aria-hidden="true"
        aria-labelledby="weeklyReport"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Weekly Report
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div dangerouslySetInnerHTML={{ __html: report }}></div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={() => {
                  setReport("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Choice Modal*/}
      <div
        className="modal fade report-modal"
        id="exampleModalToggle9"
        aria-hidden="true"
        aria-labelledby="weeklyReportChoice"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Weekly Report
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setReport("")}
              />
            </div>

            <div className="modal-body">
              <div className="option-container">
                <button
                  type="button"
                  className="option-button"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReportDateModal"
                  data-bs-toggle="modal"
                >
                  By Date
                </button>
                <button
                  type="button"
                  className="option-button"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReportPeriodModal"
                  data-bs-toggle="modal"
                >
                  By Period
                </button>
              </div>

              <div dangerouslySetInnerHTML={{ __html: report }} />
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                data-bs-dismiss="modal"
                onClick={() => setReport("")}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Date Modal */}
      <div
        className="modal fade report-modal"
        id="weeklyReportDateModal"
        aria-hidden="true"
        aria-labelledby="weeklyReportDateModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Enter Weekly Report Date
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form
                onSubmit={handleWeeklyReportSubmit}
                className="d-flex flex-column"
              >
                {/* Start Date */}
                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">
                    Start Date
                  </label>
                  <DatePicker
                    id="startDate"
                    name="startDate"
                    className="form-control w-100"
                    selected={userData.startDate}
                    onChange={handleStartDateChange}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>

                {/* End Date */}
                <div className="form-group">
                  <label htmlFor="endDate" className="form-label">
                    End Date
                  </label>
                  <DatePicker
                    id="endDate"
                    name="endDate"
                    className="form-control w-100"
                    selected={userData?.endDate}
                    onChange={handleDateChange}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>
                {/* Submit Button */}
                <button
                  type="submit"
                  className="report-button"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReport"
                  data-bs-toggle="modal"
                >
                  Generate Report
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Report Period Modal */}
      <div
        className="modal fade report-modal"
        id="weeklyReportPeriodModal"
        aria-hidden="true"
        aria-labelledby="weeklyReportDateModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Enter Weekly Report Details
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              <form
                onSubmit={handleWeeklyReportSubmit}
                className="d-flex flex-column"
              >
                {/* Period Type Selection */}
                <div className="form-group">
                  <label htmlFor="periodType" className="form-label">
                    Type:
                  </label>
                  <select
                    id="periodType"
                    name="periodType"
                    className="form-control"
                    value={userData?.periodType}
                    onChange={handleInputChange}
                  >
                    <option value="" disabled>
                      Select Type
                    </option>
                    <option value="durationPeriod">Duration Period</option>
                    <option value="financialTimeFrame">
                      Financial Time Frame Association
                    </option>
                  </select>
                </div>

                {/* Duration Period Fields */}
                {userData?.periodType === "durationPeriod" && (
                  <>
                    <div className="form-group">
                      <label htmlFor="periodStartDate" className="form-label">
                        Start Date:
                      </label>
                      <DatePicker
                        id="periodStartDate"
                        name="periodStartDate"
                        className="form-control w-100"
                        selected={userData?.periodStartDate}
                        onChange={handlePeriodStartDateChange}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-MM-yyyy"
                        locale="en-AU"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="periodEndDate" className="form-label">
                        End Date:
                      </label>
                      <DatePicker
                        id="periodEndDate"
                        name="periodEndDate"
                        className="form-control w-100"
                        selected={userData?.periodEndDate}
                        onChange={handlePeriodEndDateChange}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="dd-MM-yyyy"
                        locale="en-AU"
                      />
                    </div>
                  </>
                )}

                {/* Financial Time Frame Fields */}
                {userData?.periodType === "financialTimeFrame" && (
                  <>
                    <div className="form-group">
                      <FinancialYearField
                        id="financialYear"
                        label="Financial Year:"
                        value={userData?.financialYear ?? ""}
                        onChange={(financialYear) =>
                          setUserData((prev: any) => ({
                            ...prev,
                            financialYear,
                          }))
                        }
                        selectClassName="form-control"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="financialPeriod" className="form-label">
                        Financial Period:
                      </label>
                      <select
                        id="financialPeriod"
                        name="financialPeriod"
                        className="form-control"
                        value={userData?.financialPeriod}
                        onChange={handleInputChange}
                      >
                        <option value="" disabled>
                          Select Financial Period
                        </option>
                        <option value="january">January</option>
                        <option value="february-march">{"February - March"}</option>
                        <option value="april">April</option>
                        <option value="may-june">{"May - June"}</option>
                        <option value="june-july">{"July - June"}</option>
                        <option value="july">July</option>
                        <option value="august-september">
                          August - September
                        </option>
                        <option value="october">October</option>
                        <option value="november-december">
                          November - December
                        </option>
                      </select>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className="report-button"
                  data-bs-dismiss="modal"
                  data-bs-target="#weeklyReport"
                  data-bs-toggle="modal"
                >
                  Generate Report
                </button>
              </form>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report By Task Modal */}
      <div
        className="modal fade report-modal"
        id="reportByTaskModal"
        aria-hidden="true"
        aria-labelledby="reportByTaskModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Report By Task
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form
                onSubmit={handleTaskReportSubmit}
                className="d-flex flex-column"
              >
                <div className="form-group">
                  <label htmlFor="taskName" className="form-label">
                    Task Name
                  </label>
                  <Select
                    id="taskName"
                    options={[...(tasks || [])]
                      .sort()
                      .map((task: any) => ({ value: task, label: task }))}
                    value={
                      userData.taskName
                        ? { value: userData.taskName, label: userData.taskName }
                        : null
                    }
                    onChange={(selectedOption: any) => {
                      setUserData({
                        ...userData,
                        taskName: selectedOption?.value || "",
                      });
                    }}
                    placeholder="Select Task"
                    isClearable
                    isSearchable
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        minHeight: "38px",
                      }),
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="byTaskStartDate" className="form-label">
                    Start Date
                  </label>
                  <DatePicker
                    id="byTaskStartDate"
                    name="byTaskStartDate"
                    className="form-control w-100"
                    selected={userData.byTaskStartDate}
                    onChange={(date: Date | null) =>
                      setUserData({
                        ...userData,
                        byTaskStartDate: date || new Date(),
                      })
                    }
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="byTaskEndDate" className="form-label">
                    End Date
                  </label>
                  <DatePicker
                    id="byTaskEndDate"
                    name="byTaskEndDate"
                    className="form-control w-100"
                    selected={userData.byTaskEndDate}
                    onChange={(date: Date | null) =>
                      setUserData({
                        ...userData,
                        byTaskEndDate: date || new Date(),
                      })
                    }
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>

                <button type="submit" className="report-button">
                  Generate Report
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report By Client Group Modal */}
      <div
        className="modal fade report-modal"
        id="reportByClientGroupModal"
        aria-hidden="true"
        aria-labelledby="reportByClientGroupModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="exampleModalToggleLabel">
                Report By Client Group
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form
                onSubmit={handleClientGroupReportSubmit}
                className="d-flex flex-column"
              >
                <div className="form-group">
                  <label htmlFor="clientGroupName" className="form-label">
                    Client Group
                  </label>
                  <Select
                    id="clientGroupName"
                    options={[...clientGroups]
                      .sort()
                      .map((group: any) => ({ value: group, label: group }))}
                    value={
                      userData.clientGroupName
                        ? {
                            value: userData.clientGroupName,
                            label: userData.clientGroupName,
                          }
                        : null
                    }
                    onChange={(selectedOption: any) => {
                      setUserData({
                        ...userData,
                        clientGroupName: selectedOption?.value || "",
                      });
                    }}
                    placeholder="Select Client Group"
                    isClearable
                    isSearchable
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        minHeight: "38px",
                      }),
                    }}
                  />
                </div>

                <div className="form-group">
                  <label
                    htmlFor="byClientGroupStartDate"
                    className="form-label"
                  >
                    Start Date
                  </label>
                  <DatePicker
                    id="byClientGroupStartDate"
                    name="byClientGroupStartDate"
                    className="form-control w-100"
                    selected={userData.byClientGroupStartDate}
                    onChange={(date: Date | null) =>
                      setUserData({
                        ...userData,
                        byClientGroupStartDate: date || new Date(),
                      })
                    }
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="byClientGroupEndDate" className="form-label">
                    End Date
                  </label>
                  <DatePicker
                    id="byClientGroupEndDate"
                    name="byClientGroupEndDate"
                    className="form-control w-100"
                    selected={userData.byClientGroupEndDate}
                    onChange={(date: Date | null) =>
                      setUserData({
                        ...userData,
                        byClientGroupEndDate: date || new Date(),
                      })
                    }
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-MM-yyyy"
                    locale="en-AU"
                  />
                </div>

                <button type="submit" className="report-button">
                  Generate Report
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Sheets — custom motion-blur backdrop (not Bootstrap .modal-backdrop) */}
      {uploadSheetsBlurOpen ? (
        <div
          className="admin-motion-blur-backdrop"
          aria-hidden="true"
          onClick={handleUploadSheetsBackdropClick}
        />
      ) : null}

      {/* Upload Sheets Modal */}
      <div
        className="modal fade temporal-modal upload-modal admin-upload-sheets-modal"
        id="temporalActivityUploadModal"
        data-bs-backdrop="false"
        data-bs-keyboard="true"
        aria-hidden="true"
        aria-labelledby="temporalActivityUploadModalLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1
                className="modal-title fs-5"
                id="temporalActivityUploadModalLabel"
              >
                Upload Sheets
              </h1>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closeUploadSheetsModal}
              ></button>
            </div>
            <div className="modal-body">
              <form className="upload-form">
                {/* Upload Button 1 */}
                <div className="form-group">
                  <label htmlFor="fileUpload1" className="form-label">
                    Upload client code form status (Work):
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="fileUpload1"
                    onChange={(e: any) => {
                      setSheet1(e.target.files[0]);
                    }}
                  />
                </div>

                {/* Upload Button 2 */}
                <div className="form-group">
                  <label htmlFor="fileUpload2" className="form-label">
                    Upload client code group mapping (Client Group):
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="fileUpload2"
                    onChange={(e: any) => {
                      setSheet2(e.target.files[0]);
                    }}
                  />
                </div>

                <button
                  type="button"
                  className="upload-button"
                  onClick={submitUploadSheetsAndShowResults}
                >
                  Upload Sheets
                </button>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="cancel-button"
                onClick={closeUploadSheetsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Result Modal */}
      <div
        className="modal fade temporal-modal activity-results-modal"
        id="activityResultModal"
        aria-hidden="true"
        aria-labelledby="activityResultModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="activityResultModalLabel">
                Activity Status Report
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {trackingDataLoading ? (
                <div className="d-flex justify-content-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                trackingData && (
                  <div className="table-container">
                    {Array.isArray(trackingData?.Tracking) ? (
                      <table>
                        <thead>
                          <tr>
                            <th>Client Code</th>
                            <th>Status Change</th>
                            <th>Hours Logged</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trackingData?.Tracking?.map(
                            (item: any, index: any) => (
                              <tr key={index}>
                                <td>{item.client_code}</td>
                                <td>{item.status_change}</td>
                                <td>
                                  {item.hours_logged_in_timeframe.toFixed(2)}{" "}
                                  hours
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <p className="p-4">{trackingData?.Tracking}</p>
                    )}
                  </div>
                )
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-button" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Upload Result Modal */}
      <div
        className="modal fade temporal-modal upload-modal admin-upload-sheets-modal"
        id="activityResultUploadModal"
        data-bs-backdrop="false"
        data-bs-keyboard="true"
        aria-hidden="true"
        aria-labelledby="activityResultUploadModal"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1
                className="modal-title fs-5"
                id="activityResultUploadModalLabel"
              >
                Upload Results
              </h1>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closeUploadResultsModal}
              ></button>
            </div>
            <div className="modal-body">
              {uploadDataLoading ? (
                <div className="d-flex justify-content-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                uploadResults && (
                  <div className="results-container">
                    <p>{uploadResults}</p>
                  </div>
                )
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="action-button"
                onClick={backFromUploadResultsModal}
              >
                Back
              </button>
              <button
                type="button"
                className="cancel-button"
                onClick={closeUploadResultsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Modal */}
      <div
        className="modal fade dashboard-modal"
        id="dashboardModal"
        aria-hidden="true"
        aria-labelledby="dashboardModalLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="dashboardModalLabel">
                Dashboard
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body" style={{ height: "600px" }}>
              <form className="form-inline" onSubmit={handleGetDashboard}>
                <div className="form-group">
                  <label htmlFor="startDate">Start Date:</label>
                  <Select
                    id="startDate"
                    options={[...(availableDates || [])].map(
                      (date: string) => ({ value: date, label: date }),
                    )}
                    value={
                      selectedStartDate
                        ? { value: selectedStartDate, label: selectedStartDate }
                        : null
                    }
                    onChange={(selectedOption: any) => {
                      setSelectedStartDate(selectedOption?.value || "");
                      setHasUserSelectedDates(true);
                    }}
                    placeholder="Select Start Date"
                    isClearable
                    isSearchable
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        minHeight: "38px",
                      }),
                    }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate">End Date:</label>
                  <Select
                    id="endDate"
                    options={[...(availableDates || [])].map(
                      (date: string) => ({ value: date, label: date }),
                    )}
                    value={
                      selectedEndDate
                        ? { value: selectedEndDate, label: selectedEndDate }
                        : null
                    }
                    onChange={(selectedOption: any) => {
                      setSelectedEndDate(selectedOption?.value || "");
                      setHasUserSelectedDates(true);
                    }}
                    placeholder="Select End Date"
                    isClearable
                    isSearchable
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        minHeight: "38px",
                        overflowY: "auto",
                      }),
                    }}
                  />
                </div>

                {hasUserSelectedDates &&
                  selectedStartDate &&
                  selectedEndDate &&
                  selectedStartDate > selectedEndDate && (
                    <div className="alert alert-danger">
                      Start date must be before or equal to end date
                    </div>
                  )}

                <button
                  type="submit"
                  className="action-button"
                  disabled={
                    !selectedStartDate ||
                    !selectedEndDate ||
                    selectedStartDate > selectedEndDate
                  }
                >
                  Get Dashboard
                </button>
              </form>

              {dashboardLoading && (
                <div className="d-flex justify-content-center my-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {!dashboardLoading &&
                dashboardData &&
                dashboardData.length > 0 && (
                  <div className="table-container">
                    <table className="table table-striped table-bordered">
                      <thead>
                        <tr>
                          <th>Client Group</th>
                          <th>Activity Score</th>
                          <th>Trend</th>
                          <th>Timeline</th>
                          <th>Time Recorded</th>
                          <th>Team Count</th>
                          <th>Status Changes</th>
                          <th>Forms</th>
                          <th>Financial Report</th>
                          <th>Work Paper</th>
                          <th>Last Activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.map((item: any, index: number) => (
                          <tr
                            key={index}
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              if (item.markdown) {
                                setSelectedDashboardMarkdown(item.markdown);
                                setShowDashboardMarkdownModal(true);
                              }
                            }}
                            title={item.markdown ? "Click to view details" : ""}
                          >
                            <td>{item.ClientGroup}</td>
                            <td>
                              {typeof item.ActivityScore === "number"
                                ? item.ActivityScore.toFixed(2)
                                : item.ActivityScore}
                            </td>
                            <td>{item.Trend}</td>
                            <td className="font-monospace">
                              {item.TimelineViz}
                            </td>
                            <td>{item.TimeRecorded}</td>
                            <td>{item.TeamCount}</td>
                            <td>{item.StatusChangeDisplay}</td>
                            <td>{item.FormsDisplay}</td>
                            <td>{item?.FinancialReportDisplay}</td>
                            <td>{item?.WorkpaperDisplay}</td>
                            <td>{item.LastActivityDisplay}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

              {!dashboardLoading &&
                (!dashboardData || dashboardData.length === 0) && (
                  <div className="alert alert-info my-4">
                    No dashboard data available. Please enter the number of days
                    and click "Get Dashboard".
                  </div>
                )}
            </div>
            <div className="modal-footer">
              <button className="cancel-button" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Dev WorkLog Modal */}
      <div
        className="modal fade report-modal"
        id="devWorklogModal"
        aria-hidden="true"
        aria-labelledby="devWorklogModalLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              {showWorklogReport ? (
                <div className="d-flex align-items-center gap-2 min-w-0">
                  <button
                    type="button"
                    className="btn btn-link admin-worklog-back-btn p-0 d-inline-flex align-items-center"
                    aria-label="Back to date selection"
                    onClick={handleWorklogBack}
                  >
                    <Icon
                      icon="mdi:arrow-left"
                      width={24}
                      height={24}
                      aria-hidden
                    />
                  </button>
                  <h1
                    className="modal-title fs-5 mb-0"
                    id="devWorklogModalLabel"
                  >
                    Worklog
                  </h1>
                </div>
              ) : (
                <h1 className="modal-title fs-5" id="devWorklogModalLabel">
                  WorkLog
                </h1>
              )}
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {!showWorklogReport ? (
              <form
                className="admin-worklog-filters"
                onSubmit={(e) => void fetchDevWorklog(e)}
              >
                <div className="admin-worklog-date-row">
                  <div className="admin-worklog-date-field">
                    <label className="form-label" htmlFor="worklogStartDate">
                      Start date
                    </label>
                    <DatePicker
                      id="worklogStartDate"
                      className="form-control w-100"
                      selected={worklogStartDate}
                      onChange={(d: Date | null) => d && setWorklogStartDate(d)}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-MM-yyyy"
                      locale="en-AU"
                      disabled={worklogLoading}
                    />
                  </div>
                  <div className="admin-worklog-date-field">
                    <label className="form-label" htmlFor="worklogEndDate">
                      End date
                    </label>
                    <DatePicker
                      id="worklogEndDate"
                      className="form-control w-100"
                      selected={worklogEndDate}
                      onChange={(d: Date | null) => d && setWorklogEndDate(d)}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-MM-yyyy"
                      locale="en-AU"
                      disabled={worklogLoading}
                    />
                  </div>
                </div>
                {worklogError ? (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {worklogError}
                  </div>
                ) : null}
                <button
                  type="submit"
                  className="btn btn-primary admin-worklog-request-btn mt-3"
                  disabled={worklogLoading}
                >
                  {worklogLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Requesting…
                    </>
                  ) : (
                    "Request Worklog"
                  )}
                </button>
              </form>
              ) : (
                <div className="admin-worklog-results">
                  <DevWorklogReport data={worklogData} />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Markdown Modal */}
      <div
        className={`modal fade dashboard-modal ${
          showDashboardMarkdownModal ? "show d-block" : ""
        }`}
        id="dashboardMarkdownModal"
        tabIndex={-1}
        aria-labelledby="dashboardMarkdownModalLabel"
        aria-hidden={!showDashboardMarkdownModal}
        style={{
          background: showDashboardMarkdownModal
            ? "rgba(0,0,0,0.5)"
            : undefined,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="dashboardMarkdownModalLabel">
                Dashboard Details
              </h1>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setShowDashboardMarkdownModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              <div
                className="markdown-body"
                style={{ maxHeight: "60vh", overflowY: "auto" }}
              >
                {selectedDashboardMarkdown ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {tidyMarkdown(selectedDashboardMarkdown)}
                  </ReactMarkdown>
                ) : (
                  <div className="alert alert-info">No details available.</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowDashboardMarkdownModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
